import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faChevronRight,
  faChevronDown,
  faChartLine,
  faLayerGroup,
  faDollarSign,
  faSearch,
  faFilter,
  faFileExcel,
  faFilePdf,
  faWrench,
  faSave,
  faPlus,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import SimulationResultsTable from "../components/SimulationResultsTable";
import MonteCarloResults from "../components/MonteCarloResults";
import BridgeSelectionModal from "../components/BridgeSelectionModal";
import DamagePortionsBreakdown from "../components/DamagePortionsBreakdown";
import { exportSimulationToExcel } from "../utils/excelExport";
import { exportSimulationToPDF as exportSimulationToPDFUtil } from "../utils/pdfExport";
import {
  calculateCalendarConstraintWeeks,
  calculateGeneralCostBaseTotal,
  calculateTeamWeeklyMetrics,
  resolveTeamsAndWeeks,
} from "../utils/mobilizationSimulationUtils";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const normalizePositiveDecimal = (value, fallback) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const normalizeExposureClassCode = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const normalizeDamageCombinationCode = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const normalizeProductivityAccessMethodKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeProductivityPercentage = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    ? parsed
    : 100;
};

const buildProductivityMethodAdjustmentMap = (configuredSets) => {
  const byKey = new Map();
  const rows = Array.isArray(configuredSets?.adjustments)
    ? configuredSets.adjustments
    : [];

  rows.forEach((row) => {
    const combinationCode = normalizeDamageCombinationCode(row.combinationCode);
    const accessMethod = normalizeProductivityAccessMethodKey(
      row.accessMethodName,
    );
    if (!combinationCode || !accessMethod) return;
    byKey.set(
      `${combinationCode}|${accessMethod}`,
      normalizeProductivityPercentage(row.productivityPercentage),
    );
  });

  return byKey;
};

const getProductivityMethodAdjustmentPercent = (
  adjustmentMap,
  combinationCode,
  accessMethod,
) => {
  const normalizedCombinationCode = normalizeDamageCombinationCode(
    combinationCode,
  );
  const normalizedAccessMethod = normalizeProductivityAccessMethodKey(
    accessMethod,
  );
  if (
    !normalizedCombinationCode ||
    !normalizedAccessMethod ||
    !(adjustmentMap instanceof Map)
  ) {
    return 100;
  }
  return Number(
    adjustmentMap.get(
      `${normalizedCombinationCode}|${normalizedAccessMethod}`,
    ) ?? 100,
  );
};

const MAX_CALCULATION_LOG_ELEMENTS = 500;
const DAMAGE_ALLOCATION_MODES = Object.freeze({
  DAMAGE_SPLIT: "damage_split",
  ELEMENT_DAMAGE_LEVEL: "element_damage_level",
});

const isDamageSplitMode = (scenarioConfig) =>
  (scenarioConfig?.damageAllocationMode ||
    DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL) ===
  DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT;

const getDamageSplitValidationError = (scenarioConfig) => {
  if (!isDamageSplitMode(scenarioConfig)) return null;

  const byCat = scenarioConfig.damageSplitByCategory || {};
  const categories = Object.keys(byCat);
  if (categories.length > 0) {
    const bad = categories.filter((cat) => {
      const row = byCat[cat];
      const total =
        (Number(row?.A) || 0) +
        (Number(row?.B) || 0) +
        (Number(row?.C) || 0);
      return total !== 100;
    });
    return bad.length > 0
      ? `Damage split must total 100% per category. Check: ${bad.join(", ")}`
      : null;
  }

  const totalSplit = Object.values(scenarioConfig.damageSplit || {}).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0,
  );
  return totalSplit !== 100
    ? `Damage split must total 100% (currently ${totalSplit}%)`
    : null;
};

const EnhancedSimulationResults = () => {
  const [loading, setLoading] = useState(false);
  const [activeRunType, setActiveRunType] = useState(null);
  const [error, setError] = useState(null);

  // State management - similar to SpanSimulation
  const [bridges, setBridges] = useState([]);
  const [selectedBridgeId, setSelectedBridgeId] = useState("");
  const [selectedBridge, setSelectedBridge] = useState(null); // Full bridge object with level config
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [isSpanSelectionCollapsed] = useState(true);
  const [bridgeSelections, setBridgeSelections] = useState([]);
  const [showBridgeSelectionModal, setShowBridgeSelectionModal] =
    useState(false);
  const [editingBridgeSelectionId, setEditingBridgeSelectionId] =
    useState(null);
  const [fullBridgeSelection, setFullBridgeSelection] = useState({
    elementIds: [],
    elements: [],
    area: 0,
  });
  const [bulkSelecting, setBulkSelecting] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [isCalculationDetailsCollapsed, setIsCalculationDetailsCollapsed] =
    useState(true);

  // Simplified hierarchical selection state
  const [selectedPath, setSelectedPath] = useState({}); // { level1: id, level2: id, direction: '...', position: '...' }
  const [hierarchyData, setHierarchyData] = useState({}); // { level1: [...], level2: [...], ... }
  const [, setSelectedElementsCache] = useState({}); // Cache for selected elements: { elementId: elementData }
  const [selectionTableRows, setSelectionTableRows] = useState([]);
  const [loadingSelectionTable, setLoadingSelectionTable] = useState(false);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [descendantAreaIdsByParentId, setDescendantAreaIdsByParentId] =
    useState({});
  const [isSelectionSummaryCollapsed, setIsSelectionSummaryCollapsed] =
    useState(true);

  // Scenario configuration
  const [scenario, setScenario] = useState({
    timeHorizonYears: 30,
    damageAllocationMode: DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL,
    damageSplit: { A: 70, B: 20, C: 10 },
    damageSplitByCategory: {}, // { C2: { A, B, C }, C3: {...}, C5: {...} } – overrides per run
    discountRate: 0.03,
    estimatedPaintedAreaPercent: 30,
    mobilizationWeeksLimit: 35,
    foremansCountLimit: 1,
    layersPerDay: 1,
  });

  // Monte Carlo configuration
  const [useMonteCarloMode, setUseMonteCarloMode] = useState(false);
  const [monteCarloConfig, setMonteCarloConfig] = useState({
    iterations: 1000,
    costVariation: 15,
    degradationVariation: 20,
    discountRateVariation: 1,
    repairThreshold: 0.3,
    maintenanceThreshold: 0.1,
  });

  // Results state
  const [simulationResults, setSimulationResults] = useState(null);
  const [lastRunSelectAllMode, setLastRunSelectAllMode] = useState(false);
  const [simulationRecordId, setSimulationRecordId] = useState(null);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotSaved, setSnapshotSaved] = useState(false);
  const [showSaveSnapshotModal, setShowSaveSnapshotModal] = useState(false);
  const [snapshotTitle, setSnapshotTitle] = useState("");
  const [loadingDetailProgress, setLoadingDetailProgress] = useState(null); // { loaded: number, total: number } when loading year-by-year
  const [detailLoadMeta, setDetailLoadMeta] = useState(null); // { simulationId, totalYears }
  const [loadingMobilizationYears, setLoadingMobilizationYears] =
    useState(false);
  const [mobilizationYearInputs, setMobilizationYearInputs] = useState(null);
  const [d610Settings, setD610Settings] = useState({});
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [resultsSearchTerm, setResultsSearchTerm] = useState("");
  const [resultsFilterType, setResultsFilterType] = useState("all");
  const [showDecayPercent, setShowDecayPercent] = useState(true);
  const [expandAllRowsSignal, setExpandAllRowsSignal] = useState(0);
  const [mobilizationMasterYears, setMobilizationMasterYears] = useState({
    teamYears: [],
    productivityYears: [],
    generalCostYears: [],
    mobilizationConfigYears: [],
    productivityMethodAdjustmentSets: {
      damageCombinations: [],
      accessMethods: [],
      adjustments: [],
    },
  });

  // Create Repair Project state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    projectName: "",
    projectCode: "",
    description: "",
    plannedYear: new Date().getFullYear(),
    crewsCount: 1,
    priority: "medium",
  });

  // Optimization state
  const [showOptimization, setShowOptimization] = useState(false);
  const [optimizationParams, setOptimizationParams] = useState({
    minDecayThreshold: 0.5,
    maxDecayThreshold: 10.0,
    stepSize: 0.5,
  });
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  // Damage Split Defaults from settings (for showing default hint and prefill)
  const [damageSplitDefaultsFromSettings, setDamageSplitDefaultsFromSettings] =
    useState([]);
  const hasPrefilledDamageSplitRef = useRef(false);
  const snapshotTitleInputRef = useRef(null);

  // Fetch bridges on mount
  useEffect(() => {
    fetchBridges();
  }, []);

  useEffect(() => {
    if (showSaveSnapshotModal) {
      snapshotTitleInputRef.current?.focus();
    }
  }, [showSaveSnapshotModal]);

  // Prefill scenario defaults and load D610 settings.
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get("/settings/categories");
        if (!response.data?.success) return;
        const allSettings = response.data?.data || {};
        const projectDefaults = allSettings.project_defaults || {};
        setD610Settings(allSettings.d610_degradation_rate || {});

        const estimatedAreaRaw =
          projectDefaults?.estimated_painted_area_pct?.value;
        const estimatedArea =
          estimatedAreaRaw != null ? parseFloat(estimatedAreaRaw) : null;

        const inflationRatePctRaw =
          projectDefaults?.default_inflation_rate_pct?.value;
        const inflationRatePct =
          inflationRatePctRaw != null ? parseFloat(inflationRatePctRaw) : null;
        const configuredWeeksLimit = Number(
          projectDefaults?.default_mobilization_weeks_limit?.value,
        );
        const configuredForemansLimit = Number(
          projectDefaults?.default_foremans_count?.value,
        );
        const configuredLayersPerDay =
          projectDefaults?.default_layers_per_day?.value;

        setScenario((prev) => ({
          ...prev,
          estimatedPaintedAreaPercent:
            estimatedArea != null && !Number.isNaN(estimatedArea)
              ? Math.min(100, Math.max(0, estimatedArea))
              : prev.estimatedPaintedAreaPercent,
          discountRate:
            inflationRatePct != null && !Number.isNaN(inflationRatePct)
              ? Math.min(1, Math.max(0, inflationRatePct / 100))
              : prev.discountRate,
          layersPerDay: normalizePositiveDecimal(
            configuredLayersPerDay,
            prev.layersPerDay,
          ),
        }));

        if (Number.isFinite(configuredWeeksLimit) && configuredWeeksLimit > 0) {
          setScenario((prev) => ({
            ...prev,
            mobilizationWeeksLimit: Math.round(configuredWeeksLimit),
          }));
        }

        if (
          Number.isFinite(configuredForemansLimit) &&
          configuredForemansLimit >= 0
        ) {
          setScenario((prev) => ({
            ...prev,
            foremansCountLimit: Math.floor(configuredForemansLimit),
          }));
        }
      } catch (err) {
        console.error("Error loading simulation settings:", err);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchMobilizationMasterData = async () => {
      try {
        const [
          teamsRes,
          productivityRes,
          costsRes,
          configsRes,
          methodAdjustmentsRes,
        ] =
          await Promise.all([
            api.get(
              "/mobilization-master/painter-team-configs?grouped_by_year=true",
            ),
            api.get("/mobilization-master/productivity?grouped_by_year=true"),
            api.get("/mobilization-master/costs?grouped_by_year=true"),
            api.get(
              "/mobilization-master/mobilization-configs?grouped_by_year=true",
            ),
            api.get("/mobilization-master/productivity-method-adjustments"),
          ]);

        setMobilizationMasterYears({
          teamYears: teamsRes.data?.data || [],
          productivityYears: productivityRes.data?.data || [],
          generalCostYears: costsRes.data?.data || [],
          mobilizationConfigYears: configsRes.data?.data || [],
          productivityMethodAdjustmentSets:
            methodAdjustmentsRes.data?.data || {
              damageCombinations: [],
              accessMethods: [],
              adjustments: [],
            },
        });
      } catch (err) {
        console.error("Error loading mobilization master data:", err);
      }
    };

    fetchMobilizationMasterData();
  }, []);

  // Fetch damage split defaults and prefill per-category splits (once)
  useEffect(() => {
    const fetchDamageSplitDefaults = async () => {
      try {
        const res = await api.get("/damage-splits?is_active=true");
        if (
          res.data?.success &&
          Array.isArray(res.data.data) &&
          res.data.data.length > 0
        ) {
          const defaults = res.data.data;
          setDamageSplitDefaultsFromSettings(defaults);
          if (!hasPrefilledDamageSplitRef.current) {
            hasPrefilledDamageSplitRef.current = true;
            const byCategory = {};
            defaults.forEach((d) => {
              const cat = d.corrosionCategory || d.corrosion_category;
              if (cat && !byCategory[cat]) {
                byCategory[cat] = {
                  A: Number(d.severityA) ?? 55,
                  B: Number(d.severityB) ?? 35,
                  C: Number(d.severityC) ?? 10,
                };
              }
            });
            if (Object.keys(byCategory).length > 0) {
              setScenario((prev) => ({
                ...prev,
                damageSplitByCategory: byCategory,
                damageSplit: prev.damageSplit || { A: 70, B: 20, C: 10 },
              }));
            }
          }
        }
      } catch (err) {
        console.error("Error fetching damage split defaults:", err);
      }
    };
    fetchDamageSplitDefaults();
  }, []);

  // Fetch bridge details when selected
  useEffect(() => {
    const fetchBridgeDetails = async () => {
      if (selectedBridgeId) {
        try {
          const response = await api.get(`/bridges/${selectedBridgeId}`);
          if (response.data.success) {
            setSelectedBridge(response.data.data);
          }
        } catch (err) {
          console.error("Error fetching bridge details:", err);
        }
      } else {
        setSelectedBridge(null);
      }
    };
    fetchBridgeDetails();
  }, [selectedBridgeId]);

  // Reset hierarchical selections when bridge changes
  useEffect(() => {
    if (selectedBridgeId) {
      setSelectedPath({});
      setHierarchyData({});
      setBridgeSelections([]);
      setShowBridgeSelectionModal(false);
      setEditingBridgeSelectionId(null);
      setFullBridgeSelection({ elementIds: [], elements: [], area: 0 });
      setSelectedSpanIds([]);
      setSelectedElementsCache({});
      setSelectionTableRows([]);
      setDescendantAreaIdsByParentId({});
      setSelectAllMode(false);
      setIsSelectionSummaryCollapsed(true);
    }
  }, [selectedBridgeId]);

  // Fetch hierarchy data when bridge is selected
  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!selectedBridgeId || !selectedBridge) return;

      try {
        setLoadingHierarchy(true);

        // Fetch unique directions and positions only; level1 is fetched when user selects Item (direction)
        const directionsResponse = await api.get(
          `/new-simulation/directions/${selectedBridgeId}`,
        );
        if (directionsResponse.data.success) {
          setHierarchyData((prev) => ({
            ...prev,
            directions: directionsResponse.data.data,
          }));
        }

        const positionsResponse = await api.get(
          `/new-simulation/positions/${selectedBridgeId}`,
        );
        if (positionsResponse.data.success) {
          setHierarchyData((prev) => ({
            ...prev,
            positions: positionsResponse.data.data,
          }));
        }
      } catch (err) {
        console.error("Error fetching hierarchy:", err);
      } finally {
        setLoadingHierarchy(false);
      }
    };

    fetchHierarchy();
  }, [selectedBridgeId, selectedBridge]);

  // When path changes OR multiple elements are selected, fetch children
  // For multi-select: fetch children for ALL selected elements and merge them
  useEffect(() => {
    const fetchChildren = async () => {
      if (!selectedBridgeId) return;

      const enabledLevels = (() => {
        if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
        const levels =
          typeof selectedBridge.enabledLevels === "string"
            ? JSON.parse(selectedBridge.enabledLevels)
            : selectedBridge.enabledLevels;
        return Array.isArray(levels) ? levels : [1, 2, 3];
      })();
      const areaDataLevel = (() => {
        if (!selectedBridge || !selectedBridge.levelLabels) return 2;
        const levelLabels =
          typeof selectedBridge.levelLabels === "string"
            ? JSON.parse(selectedBridge.levelLabels)
            : selectedBridge.levelLabels;
        for (let i = 1; i <= 8; i++) {
          const levelKey = `level${i}`;
          if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
            return i;
          }
        }
        return 2;
      })();

      // Find highest selected level in path
      let highestLevel = 0;
      let parentId = null;

      // Find highest level in path
      for (let i = 1; i <= 8; i++) {
        if (selectedPath[`level${i}`]) {
          highestLevel = i;
          parentId = selectedPath[`level${i}`];
        }
      }

      // If we have multiple selected elements at area data level, use them all
      // Otherwise use the single parent from path
      let parentIds = [];
      if (highestLevel === areaDataLevel && selectedSpanIds.length > 0) {
        // Multiple elements selected - use all selected IDs
        parentIds = selectedSpanIds;
      } else if (parentId) {
        // Single parent from path
        parentIds = [parentId];
      }

      // Fetch direct children of highest selected level
      // If multiple parents selected, fetch children for all and merge
      if (highestLevel > 0 && parentIds.length > 0) {
        const childLevel = highestLevel + 1;

        // Only fetch if next level is enabled and is at or before area data level
        // (Don't fetch beyond area data level as per client requirement)
        if (childLevel <= areaDataLevel && enabledLevels.includes(childLevel)) {
          try {
            setLoadingHierarchy(true);

            // For multiple parents, fetch children for each and merge
            const allChildren = [];
            const seenChildIds = new Set();

            for (const pid of parentIds) {
              try {
                let url = `/new-simulation/hierarchy/${selectedBridgeId}?level=${childLevel}`;
                url += `&parentId=${pid}`;
                // Apply direction/position filters only at level 1; child levels use parentId only so children are visible
                if (childLevel === 1 && selectedPath.direction) {
                  url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
                }
                if (childLevel === 1 && selectedPath.position) {
                  url += `&position=${encodeURIComponent(selectedPath.position)}`;
                }

                const response = await api.get(url);
                if (
                  response.data.success &&
                  Array.isArray(response.data.data)
                ) {
                  // Merge children, avoiding duplicates
                  response.data.data.forEach((child) => {
                    if (!seenChildIds.has(child.id)) {
                      seenChildIds.add(child.id);
                      allChildren.push(child);
                    }
                  });
                }
              } catch (err) {
                console.error(
                  `Error fetching children for parent ${pid} at level ${childLevel}:`,
                  err,
                );
              }
            }

            // Update hierarchy data with merged children (set even when empty so "No items" shows)
            setHierarchyData((prev) => ({
              ...prev,
              [`level${childLevel}`]: allChildren,
            }));
          } catch (err) {
            console.error(`Error fetching level ${childLevel}:`, err);
          } finally {
            setLoadingHierarchy(false);
          }
        }
      }

      // Also ensure area data level is fetched if we have a parent path to it
      // This handles cases where area data level might not be the immediate next level
      const areaDataLevelNum = areaDataLevel;
      if (areaDataLevelNum > 0 && enabledLevels.includes(areaDataLevelNum)) {
        // Find the parent of area data level
        let areaDataParentId = null;
        for (let i = areaDataLevelNum - 1; i >= 1; i--) {
          if (selectedPath[`level${i}`]) {
            areaDataParentId = selectedPath[`level${i}`];
            break;
          }
        }

        // If we have a parent and area data level isn't already loaded, fetch it
        if (areaDataParentId) {
          try {
            let url = `/new-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevelNum}`;
            url += `&parentId=${areaDataParentId}`;
            // Direction/position filters only at level 1; deeper levels use parentId only
            if (areaDataLevelNum === 1 && selectedPath.direction) {
              url += `&direction=${encodeURIComponent(selectedPath.direction)}`;
            }
            if (areaDataLevelNum === 1 && selectedPath.position) {
              url += `&position=${encodeURIComponent(selectedPath.position)}`;
            }

            const response = await api.get(url);
            if (response.data.success) {
              setHierarchyData((prev) => ({
                ...prev,
                [`level${areaDataLevelNum}`]: response.data.data,
              }));
            }
          } catch (err) {
            console.error(
              `Error fetching area data level ${areaDataLevelNum}:`,
              err,
            );
          }
        }
      }
    };

    fetchChildren();
  }, [selectedPath, selectedBridgeId, selectedBridge, selectedSpanIds]); // Include selectedSpanIds to handle multi-select

  // Helper to get enabled levels array
  const getEnabledLevels = useCallback(() => {
    if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
    const enabledLevels =
      typeof selectedBridge.enabledLevels === "string"
        ? JSON.parse(selectedBridge.enabledLevels)
        : selectedBridge.enabledLevels;
    return Array.isArray(enabledLevels) ? enabledLevels : [1, 2, 3];
  }, [selectedBridge]);

  // Helper to get level label (user-friendly: "Direction" -> "Item")
  const getLevelLabel = useCallback(
    (levelNum) => {
      if (!selectedBridge || !selectedBridge.levelLabels)
        return `Level ${levelNum}`;
      const levelLabels =
        typeof selectedBridge.levelLabels === "string"
          ? JSON.parse(selectedBridge.levelLabels)
          : selectedBridge.levelLabels;
      const levelKey = `level${levelNum}`;
      const label = levelLabels[levelKey]?.label || `Level ${levelNum}`;
      return label === "Direction" ? "Item" : label;
    },
    [selectedBridge],
  );

  // Raw bridge level label without UI aliasing (used by Selection Summary table headers).
  const getRawLevelLabel = (levelNum) => {
    if (!selectedBridge || !selectedBridge.levelLabels)
      return `Level ${levelNum}`;
    const levelLabels =
      typeof selectedBridge.levelLabels === "string"
        ? JSON.parse(selectedBridge.levelLabels)
        : selectedBridge.levelLabels;
    const levelKey = `level${levelNum}`;
    return levelLabels[levelKey]?.label || `Level ${levelNum}`;
  };

  // Helper to find which level holds area data
  const getAreaDataLevel = useCallback(() => {
    if (!selectedBridge || !selectedBridge.levelLabels) return 2; // Default fallback
    const levelLabels =
      typeof selectedBridge.levelLabels === "string"
        ? JSON.parse(selectedBridge.levelLabels)
        : selectedBridge.levelLabels;

    for (let i = 1; i <= 8; i++) {
      const levelKey = `level${i}`;
      if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
        return i;
      }
    }
    return 2; // Default fallback
  }, [selectedBridge]);

  // Levels to show as listbox columns after Item and Position. Skip levels whose label is Item/Direction or Position so we don't repeat; always include area data level.
  const getLevelsToShowAfterFilters = useCallback(() => {
    const areaLevel = getAreaDataLevel();
    const filtered = getEnabledLevels()
      .filter((l) => l <= areaLevel)
      .filter((l) => {
        const label = getLevelLabel(l);
        return (
          (label !== "Item" && label !== "Direction" && label !== "Position") ||
          l === areaLevel
        );
      });
    return filtered.length > 0 ? filtered : [areaLevel];
  }, [getAreaDataLevel, getEnabledLevels, getLevelLabel]);

  const levelsToShowAfterFilters = useMemo(
    () => getLevelsToShowAfterFilters(),
    [getLevelsToShowAfterFilters],
  );
  const penultimateLevel =
    levelsToShowAfterFilters.length >= 2
      ? levelsToShowAfterFilters[levelsToShowAfterFilters.length - 2]
      : null;
  const firstVisibleLevel = levelsToShowAfterFilters[0] || null;

  const activeBridgeSelections = useMemo(
    () => bridgeSelections.filter((selection) => selection.active),
    [bridgeSelections],
  );

  const activeSelectedElementIds = useMemo(
    () =>
      Array.from(
        new Set(
          activeBridgeSelections.flatMap((selection) => selection.elementIds || []),
        ),
      ),
    [activeBridgeSelections],
  );

  const activeSelectionArea = useMemo(
    () =>
      activeBridgeSelections.reduce(
        (sum, selection) => sum + (Number(selection.area) || 0),
        0,
      ),
    [activeBridgeSelections],
  );

  useEffect(() => {
    const selectedIdsForMode = selectAllMode
      ? fullBridgeSelection.elementIds
      : activeSelectedElementIds;

    setSelectedSpanIds(selectedIdsForMode);

    const cache = {};
    if (selectAllMode) {
      (fullBridgeSelection.elements || []).forEach((element) => {
        if (element?.id != null) {
          cache[element.id] = element;
        }
      });
    } else {
      activeBridgeSelections.forEach((selection) => {
        (selection.elements || []).forEach((element) => {
          if (element?.id != null) {
            cache[element.id] = element;
          }
        });
      });
    }
    setSelectedElementsCache(cache);
  }, [
    activeBridgeSelections,
    activeSelectedElementIds,
    fullBridgeSelection,
    selectAllMode,
  ]);

  const selectionSummaryLevels = getEnabledLevels()
    .filter((levelNum) => levelNum <= getAreaDataLevel())
    .sort((a, b) => a - b);

  const totalAreaDataEntityLabel = useMemo(() => {
    const singular = String(
      getRawLevelLabel(getAreaDataLevel()) || "Span",
    ).trim();
    if (!singular) return "Spans";
    return singular.toLowerCase().endsWith("s") ? singular : `${singular}s`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBridge, getAreaDataLevel]);

  const selectionSummaryText = `${selectedSpanIds.length} ${totalAreaDataEntityLabel.toLowerCase()} selected`;
  const selectionAreaText = `${(
    selectAllMode ? fullBridgeSelection.area || 0 : activeSelectionArea || 0
  ).toFixed(2)} m²`;

  const handleOpenAddBridgeSelection = () => {
    setEditingBridgeSelectionId(null);
    setShowBridgeSelectionModal(true);
  };

  const handleOpenEditBridgeSelection = (selectionId) => {
    setEditingBridgeSelectionId(selectionId);
    setShowBridgeSelectionModal(true);
  };

  const handleCloseBridgeSelectionModal = () => {
    setShowBridgeSelectionModal(false);
    setEditingBridgeSelectionId(null);
  };

  const handleSaveBridgeSelection = (selectionDetails) => {
    const savedAt = Date.now();
    setBridgeSelections((prevSelections) => {
      if (editingBridgeSelectionId) {
        return prevSelections.map((selection) =>
          selection.id === editingBridgeSelectionId
            ? {
                ...selection,
                ...selectionDetails,
                updatedAt: savedAt,
              }
            : selection,
        );
      }

      return [
        ...prevSelections,
        {
          id: `bridge-selection-${savedAt}`,
          active: true,
          ...selectionDetails,
          createdAt: savedAt,
          updatedAt: savedAt,
        },
      ];
    });
    handleCloseBridgeSelectionModal();
  };

  const handleToggleBridgeSelection = (selectionId) => {
    setBridgeSelections((prevSelections) =>
      prevSelections.map((selection) =>
        selection.id === selectionId
          ? { ...selection, active: !selection.active }
          : selection,
      ),
    );
  };

  const handleDeleteBridgeSelection = (selectionId) => {
    setBridgeSelections((prevSelections) =>
      prevSelections.filter((selection) => selection.id !== selectionId),
    );
    if (editingBridgeSelectionId === selectionId) {
      handleCloseBridgeSelectionModal();
    }
  };

  const editingBridgeSelection = bridgeSelections.find(
    (selection) => selection.id === editingBridgeSelectionId,
  );

  // Build a single selection table with full path columns up to area-data level.
  useEffect(() => {
    const buildSelectionTableRows = async () => {
      if (
        !selectedBridgeId ||
        selectedSpanIds.length === 0
      ) {
        setSelectionTableRows([]);
        return;
      }

      try {
        setLoadingSelectionTable(true);
        const response = await api.post("/new-simulation/hierarchy-structure", {
          elementIds: selectedSpanIds,
          bridgeId: Number(selectedBridgeId),
        });

        if (!response.data?.success || !response.data?.data?.hierarchy) {
          setSelectionTableRows([]);
          return;
        }

        const roots = response.data.data.hierarchy;
        const rows = [];
        const getName = (n) =>
          n?.name || n?.element_name || n?.code || n?.element_code || "";

        const visit = (node, context = {}) => {
          const levelNum = node.levelNumber || node.level_number;
          const current = { ...context };

          if (levelNum != null) {
            current[`level${levelNum}`] = getName(node);
          }

          if (node.direction && !current.direction)
            current.direction = node.direction;
          if (node.position && !current.position)
            current.position = node.position;

          if (node.isSelected) {
            rows.push({
              id: node.id,
              direction: current.direction || "-",
              position: current.position || "-",
              levels: { ...current },
              area: parseFloat(node.area || node.total_area || 0) || 0,
            });
          }

          (node.children || []).forEach((child) => visit(child, current));
        };

        roots.forEach((root) => visit(root, {}));
        rows.sort((a, b) =>
          String(a.id).localeCompare(String(b.id), undefined, {
            numeric: true,
          }),
        );
        setSelectionTableRows(rows);
      } catch (err) {
        console.error("Error building selection table rows:", err);
        setSelectionTableRows([]);
      } finally {
        setLoadingSelectionTable(false);
      }
    };

    buildSelectionTableRows();
  }, [selectedSpanIds, selectedBridgeId]);

  useEffect(() => {
    if (!penultimateLevel) return;

    const selectedParentId = selectedPath[`level${penultimateLevel}`];
    const areaLevelKey = `level${getAreaDataLevel()}`;
    const visibleAreaIds = (hierarchyData[areaLevelKey] || [])
      .map((element) => element.id)
      .filter(Boolean);

    if (selectedParentId && visibleAreaIds.length > 0) {
      setDescendantAreaIdsByParentId((prev) => ({
        ...prev,
        [selectedParentId]: visibleAreaIds,
      }));
    }
  }, [getAreaDataLevel, hierarchyData, penultimateLevel, selectedPath]);

  // Direction listbox: select direction → clear levels, fetch positions for direction and level1 filtered by direction
  const handleDirectionListboxSelect = (direction) => {
    const newPath = { ...selectedPath };
    if (direction) {
      newPath.direction = direction;
    } else {
      delete newPath.direction;
    }
    delete newPath.position;
    for (let i = 1; i <= 8; i++) {
      delete newPath[`level${i}`];
    }
    setSelectedPath(newPath);
    setHierarchyData((prev) => {
      const next = { ...prev };
      for (let i = 1; i <= 8; i++) delete next[`level${i}`];
      return next;
    });
    if (!selectedBridgeId) return;
    (async () => {
      try {
        setLoadingHierarchy(true);
        if (direction) {
          const [posRes, level1Res] = await Promise.all([
            api.get(
              `/new-simulation/positions/${selectedBridgeId}?direction=${encodeURIComponent(direction)}`,
            ),
            api.get(
              `/new-simulation/hierarchy/${selectedBridgeId}?level=1&direction=${encodeURIComponent(direction)}`,
            ),
          ]);
          let positions =
            posRes.data.success && Array.isArray(posRes.data.data)
              ? posRes.data.data
              : [];
          if (positions.length === 0) {
            const fallbackPosRes = await api.get(
              `/new-simulation/positions/${selectedBridgeId}`,
            );
            if (
              fallbackPosRes.data.success &&
              Array.isArray(fallbackPosRes.data.data)
            ) {
              positions = fallbackPosRes.data.data;
            }
          }
          if (posRes.data.success) {
            setHierarchyData((prev) => ({ ...prev, positions }));
          }
          if (level1Res.data.success) {
            setHierarchyData((prev) => ({
              ...prev,
              level1: level1Res.data.data,
            }));
          }
        } else {
          const [posRes, level1Res] = await Promise.all([
            api.get(`/new-simulation/positions/${selectedBridgeId}`),
            api.get(`/new-simulation/hierarchy/${selectedBridgeId}?level=1`),
          ]);
          if (posRes.data.success) {
            setHierarchyData((prev) => ({
              ...prev,
              positions: posRes.data.data,
            }));
          }
          if (level1Res.data.success) {
            setHierarchyData((prev) => ({
              ...prev,
              level1: level1Res.data.data,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching after direction select:", err);
      } finally {
        setLoadingHierarchy(false);
      }
    })();
  };

  // Position listbox: select position → clear level1+, fetch level1 filtered by direction+position; auto-set level1/level2 when single so Frame column gets data
  const handlePositionListboxSelect = (position) => {
    const newPath = { ...selectedPath };
    if (position) {
      newPath.position = position;
    } else {
      delete newPath.position;
    }
    for (let i = 1; i <= 8; i++) {
      delete newPath[`level${i}`];
    }
    setSelectedPath(newPath);
    setHierarchyData((prev) => {
      const next = { ...prev };
      for (let i = 1; i <= 8; i++) delete next[`level${i}`];
      return next;
    });
    if (!selectedBridgeId) return;
    (async () => {
      try {
        setLoadingHierarchy(true);
        let url = `/new-simulation/hierarchy/${selectedBridgeId}?level=1`;
        if (newPath.direction)
          url += `&direction=${encodeURIComponent(newPath.direction)}`;
        if (position) url += `&position=${encodeURIComponent(position)}`;
        const res = await api.get(url);
        if (!res.data.success) {
          setLoadingHierarchy(false);
          return;
        }
        const level1Data = res.data.data || [];
        setHierarchyData((prev) => ({ ...prev, level1: level1Data }));

        if (level1Data.length === 1 && newPath.direction) {
          const level1Id = level1Data[0].id;
          setSelectedPath((prev) => ({ ...prev, level1: level1Id }));
          const l2Res = await api.get(
            `/new-simulation/hierarchy/${selectedBridgeId}?level=2&parentId=${level1Id}`,
          );
          if (l2Res.data.success && Array.isArray(l2Res.data.data)) {
            const level2Data = l2Res.data.data;
            setHierarchyData((prev) => ({ ...prev, level2: level2Data }));

            const normalizePosition = (v) =>
              (v || "").toString().trim().toLowerCase();
            let selectedLevel2 = null;

            if (position) {
              const targetPos = normalizePosition(position);
              selectedLevel2 =
                level2Data.find((el) => {
                  const candidates = [
                    el.position,
                    el.element_name,
                    el.element_code,
                  ]
                    .map(normalizePosition)
                    .filter(Boolean);
                  return candidates.includes(targetPos);
                }) || null;
            }

            if (!selectedLevel2 && level2Data.length === 1) {
              selectedLevel2 = level2Data[0];
            }

            if (selectedLevel2) {
              const level2Id = selectedLevel2.id;
              setSelectedPath((prev) => ({
                ...prev,
                level1: level1Id,
                level2: level2Id,
              }));
              const l3Res = await api.get(
                `/new-simulation/hierarchy/${selectedBridgeId}?level=3&parentId=${level2Id}`,
              );
              if (l3Res.data.success && Array.isArray(l3Res.data.data)) {
                setHierarchyData((prev) => ({
                  ...prev,
                  level3: l3Res.data.data,
                }));
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching after position select:", err);
      } finally {
        setLoadingHierarchy(false);
      }
    })();
  };

  // Simple: Click parent → update path → fetch children
  // Direction/Position filters are manual-only (dropdowns); we do not set them from the selected row.
  const handleLevelSelection = (level, elementId) => {
    const areaDataLevel = getAreaDataLevel();

    // Don't handle area data level here - that's handled separately
    if (level === areaDataLevel) return;

    const newPath = { ...selectedPath };

    if (elementId) {
      // Set this level in path only; do not sync direction/position from element (filters are manual-only)
      newPath[`level${level}`] = elementId;

      // Clear all levels below this one
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
    } else {
      // Deselecting - clear this and all children
      delete newPath[`level${level}`];
      for (let i = level + 1; i <= 8; i++) {
        delete newPath[`level${i}`];
        const newHierarchyData = { ...hierarchyData };
        delete newHierarchyData[`level${i}`];
        setHierarchyData(newHierarchyData);
      }
      // Clear area selections only if no parent path exists
      if (Object.keys(newPath).length === 0) {
        setSelectedSpanIds([]);
      }
    }

    setSelectedPath(newPath);
  };

  // Simple: Toggle area data element selection (multi-select, persists across navigation)
  // Also loads child levels when an element is selected
  const handleAreaDataSelection = (elementId, isChecked) => {
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;

    if (isChecked) {
      setSelectedSpanIds((prev) => {
        if (!prev.includes(elementId)) {
          return [...prev, elementId];
        }
        return prev;
      });

      // Cache the element data so it persists when hierarchy data is cleared
      const element = hierarchyData[areaLevelKey]?.find(
        (el) => el.id === elementId,
      );
      if (element) {
        setSelectedElementsCache((prev) => ({
          ...prev,
          [elementId]: element,
        }));
      }

      // Also set in path to trigger child level fetch
      const newPath = { ...selectedPath };
      newPath[`level${areaDataLevel}`] = elementId;
      setSelectedPath(newPath);
    } else {
      setSelectedSpanIds((prev) => prev.filter((id) => id !== elementId));

      // Remove from cache
      setSelectedElementsCache((prev) => {
        const newCache = { ...prev };
        delete newCache[elementId];
        return newCache;
      });

      // Remove from path if it was the tracked element
      if (selectedPath[`level${areaDataLevel}`] === elementId) {
        const newPath = { ...selectedPath };
        delete newPath[`level${areaDataLevel}`];
        setSelectedPath(newPath);
      }
    }
  };

  const fetchBridges = async () => {
    try {
      const response = await api.get("/bridges?limit=100");
      if (response.data.success) {
        setBridges(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching bridges:", err);
      setError("Failed to load bridges");
    }
  };

  // Note: We no longer fetch all spans upfront - we use hierarchical selection instead

  const handleSelectAll = async () => {
    // Select all elements at the area data level for the entire bridge (not just current path)
    setSelectAllMode(true);
    if (!selectedBridgeId || !selectedBridge) return;
    if (fullBridgeSelection.elementIds.length > 0) return;

    const areaDataLevel = getAreaDataLevel();
    try {
      setBulkSelecting(true);
      const url = `/new-simulation/hierarchy/${selectedBridgeId}?level=${areaDataLevel}`;
      const response = await api.get(url);
      if (response.data.success) {
        const allElements = response.data.data;
        const allIds = allElements.map((el) => el.id);
        const normalizedElements = allElements.map((element) => ({
            id: element.id,
            element_code: element.code || element.element_code,
            element_name: element.name || element.element_name,
            total_area: element.area || element.total_area,
        }));
        const totalArea = normalizedElements.reduce((sum, element) => {
          const area = parseFloat(element.total_area || 0);
          return Number.isNaN(area) ? sum : sum + area;
        }, 0);

        setFullBridgeSelection({
          elementIds: allIds,
          elements: normalizedElements,
          area: totalArea,
        });
      }
    } catch (err) {
      console.error("Error selecting all:", err);
      setSelectAllMode(false);
    } finally {
      setBulkSelecting(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectAllMode(false);
    setDescendantAreaIdsByParentId({});
    // Do not clear selectedPath so the hierarchy and panel bay list stay visible and clickable
  };

  const applyBulkAreaSelection = useCallback((areaElements, isChecked) => {
    const uniqueElements = [];
    const seen = new Set();
    (areaElements || []).forEach((el) => {
      if (!el || !el.id || seen.has(el.id)) return;
      seen.add(el.id);
      uniqueElements.push(el);
    });

    if (uniqueElements.length === 0) return;

    const ids = uniqueElements.map((el) => el.id);
    const idSet = new Set(ids);

    if (isChecked) {
      setSelectedSpanIds((prev) =>
        Array.from(new Set([...(prev || []), ...ids])),
      );
      setSelectedElementsCache((prev) => {
        const next = { ...(prev || {}) };
        uniqueElements.forEach((el) => {
          next[el.id] = el;
        });
        return next;
      });
    } else {
      setSelectedSpanIds((prev) => (prev || []).filter((id) => !idSet.has(id)));
      setSelectedElementsCache((prev) => {
        const next = { ...(prev || {}) };
        ids.forEach((id) => delete next[id]);
        return next;
      });
    }
  }, []);

  const collectAreaElementsFromParentIds = useCallback(
    async (startLevel, parentIds) => {
      if (!selectedBridgeId) return [];
      const areaDataLevel = getAreaDataLevel();
      const uniqueParentIds = Array.from(
        new Set((parentIds || []).filter(Boolean)),
      );

      if (uniqueParentIds.length === 0) return [];

      if (startLevel === areaDataLevel) {
        const areaLevelKey = `level${areaDataLevel}`;
        const loaded = hierarchyData[areaLevelKey] || [];
        const byId = new Map(loaded.map((el) => [el.id, el]));
        return uniqueParentIds.map((id) => byId.get(id) || { id });
      }

      let currentParentIds = uniqueParentIds;
      let currentElements = [];

      for (let level = startLevel + 1; level <= areaDataLevel; level++) {
        const responses = await Promise.all(
          currentParentIds.map((pid) =>
            api.get(
              `/new-simulation/hierarchy/${selectedBridgeId}?level=${level}&parentId=${pid}`,
            ),
          ),
        );

        const merged = [];
        const seen = new Set();
        responses.forEach((res) => {
          if (!res.data?.success || !Array.isArray(res.data.data)) return;
          res.data.data.forEach((el) => {
            if (!el?.id || seen.has(el.id)) return;
            seen.add(el.id);
            merged.push(el);
          });
        });

        currentElements = merged;
        currentParentIds = merged.map((el) => el.id);

        if (currentParentIds.length === 0) break;
      }

      return currentElements;
    },
    [selectedBridgeId, getAreaDataLevel, hierarchyData],
  );

  const handleLevelCascadeSelectAll = useCallback(
    async (levelNum, isChecked) => {
      const levelKey = `level${levelNum}`;
      const elements = hierarchyData[levelKey] || [];
      const ids = elements.map((el) => el.id).filter(Boolean);
      if (!ids.length) return;

      try {
        setBulkSelecting(true);
        const areaElements = await collectAreaElementsFromParentIds(
          levelNum,
          ids,
        );
        applyBulkAreaSelection(areaElements, isChecked);
      } catch (err) {
        console.error(`Error cascading select all for level ${levelNum}:`, err);
      } finally {
        setBulkSelecting(false);
      }
    },
    [hierarchyData, collectAreaElementsFromParentIds, applyBulkAreaSelection],
  );

  const handlePositionCascadeSelectAll = useCallback(
    async (isChecked) => {
      if (!selectedBridgeId || !selectedPath.direction) return;

      try {
        setBulkSelecting(true);
        const level1Res = await api.get(
          `/new-simulation/hierarchy/${selectedBridgeId}?level=1&direction=${encodeURIComponent(selectedPath.direction)}`,
        );

        const level1Elements =
          level1Res.data?.success && Array.isArray(level1Res.data.data)
            ? level1Res.data.data
            : [];

        if (!level1Elements.length) return;

        const areaElements = await collectAreaElementsFromParentIds(
          1,
          level1Elements.map((el) => el.id),
        );
        applyBulkAreaSelection(areaElements, isChecked);
      } catch (err) {
        console.error("Error cascading select all for positions:", err);
      } finally {
        setBulkSelecting(false);
      }
    },
    [
      selectedBridgeId,
      selectedPath.direction,
      collectAreaElementsFromParentIds,
      applyBulkAreaSelection,
    ],
  );

  const handlePenultimateSelection = useCallback(
    async (levelNum, element, isChecked) => {
      try {
        setBulkSelecting(true);
        const areaElements = await collectAreaElementsFromParentIds(levelNum, [
          element.id,
        ]);
        setDescendantAreaIdsByParentId((prev) => ({
          ...prev,
          [element.id]: areaElements.map((item) => item.id).filter(Boolean),
        }));
        applyBulkAreaSelection(areaElements, isChecked);
      } catch (err) {
        console.error("Error selecting descendants for simulation:", err);
      } finally {
        setBulkSelecting(false);
      }
    },
    [applyBulkAreaSelection, collectAreaElementsFromParentIds],
  );

  const handleScenarioChange = (field, value) => {
    setScenario((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDamageSplitByCategoryChange = (category, level, value) => {
    const num = parseInt(value, 10);
    if (Number.isNaN(num)) return;
    setScenario((prev) => {
      const byCat = { ...(prev.damageSplitByCategory || {}) };
      const current = byCat[category] || { A: 70, B: 20, C: 10 };
      byCat[category] = { ...current, [level]: num };
      return { ...prev, damageSplitByCategory: byCat };
    });
  };

  const handleDamageAllocationModeChange = (mode) => {
    setScenario((prev) => ({
      ...prev,
      damageAllocationMode: mode,
    }));
  };

  const handleCreateRepairProject = async () => {
    if (
      !projectFormData.projectName ||
      !projectFormData.plannedYear ||
      !selectedBridge
    ) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get elements that have repairs in the selected year (plannedYear)
      const plannedYear = projectFormData.plannedYear;
      const startYear =
        simulationResults.simulationStartYear != null &&
        simulationResults.simulationStartYear > 0
          ? simulationResults.simulationStartYear
          : new Date().getFullYear();
      const yearIndex = plannedYear - startYear + 1;

      // Find the year result for the planned year
      const yearResult = simulationResults.yearlyResults?.find(
        (yr) => yr.year === yearIndex,
      );

      if (!yearResult || yearResult.totalCost === 0) {
        setError(
          `No repairs found for year ${plannedYear}. Please select a year with repair costs.`,
        );
        setLoading(false);
        return;
      }

      // Get elements with repairs in this year
      const elementCosts = {}; // Track costs per element

      if (yearResult.spanResults) {
        yearResult.spanResults.forEach((spanResult) => {
          if (spanResult.totalCost > 0) {
            if (!elementCosts[spanResult.spanId]) {
              elementCosts[spanResult.spanId] = {
                id: spanResult.spanId,
                code: spanResult.spanCode,
                name: spanResult.spanName,
                totalCost: 0,
              };
            }
            elementCosts[spanResult.spanId].totalCost += spanResult.totalCost;
          }
        });
      }

      const elementIds = Object.keys(elementCosts).map((id) => parseInt(id));

      if (elementIds.length === 0) {
        setError("No elements with repairs found in selected years");
        return;
      }

      // Get bridge ID from the elements (more reliable than selectedBridge)
      // Fetch bridge ID from the first element to ensure we use the correct bridge
      let actualBridgeId = null;

      try {
        // Get bridgeId from the first element using the existing API
        const elementResponse = await api.get(
          `/bridge-structure/${elementIds[0]}`,
        );
        console.log("Element API response:", elementResponse.data);
        if (
          elementResponse.data &&
          elementResponse.data.success &&
          elementResponse.data.data
        ) {
          const elementData = elementResponse.data.data;
          // Check both possible field names (camelCase from Sequelize, snake_case from raw query)
          if (
            elementData.bridgeId !== undefined &&
            elementData.bridgeId !== null
          ) {
            actualBridgeId = parseInt(elementData.bridgeId);
          } else if (
            elementData.bridge_id !== undefined &&
            elementData.bridge_id !== null
          ) {
            actualBridgeId = parseInt(elementData.bridge_id);
          }
        }
      } catch (err) {
        console.error("Error fetching element bridge ID from API:", err);
      }

      // Fallback: try to get from simulation results spans
      if (
        !actualBridgeId &&
        simulationResults.spans &&
        simulationResults.spans.length > 0
      ) {
        const firstSpan = simulationResults.spans[0];
        if (firstSpan.bridgeId !== undefined && firstSpan.bridgeId !== null) {
          actualBridgeId = parseInt(firstSpan.bridgeId);
        } else if (
          firstSpan.bridge_id !== undefined &&
          firstSpan.bridge_id !== null
        ) {
          actualBridgeId = parseInt(firstSpan.bridge_id);
        }
      }

      // Last fallback: use selectedBridge
      if (!actualBridgeId) {
        actualBridgeId = selectedBridge?.id
          ? parseInt(selectedBridge.id)
          : null;
        console.warn("Using selected bridge ID as fallback:", actualBridgeId);
      }

      if (!actualBridgeId) {
        setError(
          "Could not determine bridge ID. Please ensure elements belong to a bridge.",
        );
        setLoading(false);
        return;
      }

      console.log(
        "Using bridge ID:",
        actualBridgeId,
        "for elements:",
        elementIds,
      );

      // Calculate total estimated cost
      const totalEstimatedCost = Object.values(elementCosts).reduce(
        (sum, el) => sum + el.totalCost,
        0,
      );

      // Create repair project
      const projectData = {
        bridgeId: actualBridgeId,
        projectName: projectFormData.projectName,
        projectCode: projectFormData.projectCode || undefined,
        description: projectFormData.description || undefined,
        spanIds: elementIds,
        plannedYear: projectFormData.plannedYear,
        crewsCount: projectFormData.crewsCount,
        priority: projectFormData.priority,
        estimatedCost: totalEstimatedCost,
        notes: `Created from simulation results. Year: ${plannedYear}. Total elements: ${elementIds.length}.`,
      };

      const response = await api.post("/repair-projects", projectData);

      if (response.data.success) {
        setShowCreateProjectModal(false);
        setProjectFormData({
          projectName: "",
          projectCode: "",
          description: "",
          plannedYear: new Date().getFullYear(),
          crewsCount: 1,
          priority: "medium",
        });

        // Navigate to the new project or show success
        const projectId = response.data.data.id;
        if (
          window.confirm(
            `Repair project created successfully! Would you like to view it?`,
          )
        ) {
          window.location.href = `/repair-projects/${projectId}`;
        }
      } else {
        setError(response.data.message || "Failed to create repair project");
      }
    } catch (err) {
      console.error("Error creating repair project:", err);
      setError(
        err.response?.data?.message || "Failed to create repair project",
      );
    } finally {
      setLoading(false);
    }
  };

  const runOptimization = async () => {
    const spanIdsForRun = selectedSpanIds;
    if (spanIdsForRun.length === 0) {
      setError("Please add at least one active bridge selection");
      return;
    }

    const damageSplitValidationError = getDamageSplitValidationError(scenario);
    if (damageSplitValidationError) {
      setError(damageSplitValidationError);
      return;
    }

    try {
      setOptimizing(true);
      setError(null);
      setOptimizationResults(null);
      setSimulationResults(null);

      const response = await api.post("/optimization/run", {
        spanIds: spanIdsForRun,
        scenario: scenario,
        optimizationParams: optimizationParams,
      });

      if (response.data.success) {
        setOptimizationResults(response.data.data);
        // Set the optimal simulation as the main results
        if (response.data.data.optimalSimulation) {
          const optimalSim = response.data.data.optimalSimulation;

          // Transform hierarchy asynchronously (same as regular simulation)
          try {
            const hierarchy = await transformToHierarchy(optimalSim);
            optimalSim.hierarchy = hierarchy;
          } catch (err) {
            console.error(
              "Error transforming hierarchy for optimal simulation:",
              err,
            );
            optimalSim.hierarchy = null;
          }

          setSimulationResults(optimalSim);
        }
      }
    } catch (err) {
      console.error("Error running optimization:", err);
      setError(err.response?.data?.message || "Failed to run optimization");
    } finally {
      setOptimizing(false);
    }
  };

  const runSimulation = async ({ auditMode = false } = {}) => {
    const spanIdsForRun = selectedSpanIds;
    if (spanIdsForRun.length === 0) {
      setError("Please add at least one active bridge selection");
      return;
    }

    if (auditMode && useMonteCarloMode) {
      setError("Audit mode uses the deterministic simulation engine.");
      return;
    }

    if (auditMode && selectAllMode) {
      setError("Audit mode requires manual bridge selection.");
      return;
    }

    if (auditMode && spanIdsForRun.length > MAX_CALCULATION_LOG_ELEMENTS) {
      setError(
        `Audit mode is limited to ${MAX_CALCULATION_LOG_ELEMENTS.toLocaleString()} manually selected ${totalAreaDataEntityLabel.toLowerCase()}.`,
      );
      return;
    }

    // Warn about large simulations
    if (!auditMode && spanIdsForRun.length > 100) {
      const confirmed = window.confirm(
        `Large Simulation Warning\n\n` +
          `You are about to simulate ${spanIdsForRun.length} elements.\n` +
          `This may take up to 1-3 minutes to complete on most complex bridges.\n\n` +
          `Do you want to continue?`,
      );
      if (!confirmed) {
        return;
      }
    }

    const damageSplitValidationError = getDamageSplitValidationError(scenario);
    if (damageSplitValidationError) {
      setError(damageSplitValidationError);
      return;
    }

    try {
      setLoading(true);
      setActiveRunType(auditMode ? "audit" : "simulation");
      setError(null);
      setSimulationResults(null);
      setSnapshotSaved(false);
      setSimulationRecordId(null);
      setDetailLoadMeta(null);
      setMobilizationYearInputs(null);
      setLastRunSelectAllMode(selectAllMode);
      setExpandedItems(new Set()); // Clear expanded items for new simulation

      const response = await api.post(
        "/new-simulation/run",
        {
          spanIds: spanIdsForRun,
          scenario,
          useCustomRepairSteps: false,
          auditMode,
        },
        {
          timeout: 600000, // 10 minutes for full-bridge simulations
        },
      );

      if (response.data.success) {
        const simulationId =
          response.data.simulationId ??
          response.data.data?.id ??
          response.data.data?.simulationId;
        if (simulationId == null) {
          setError("Simulation completed but no simulation ID returned.");
          return;
        }
        setSimulationRecordId(simulationId);
        // Fetch summary first (small payload) so UI shows summary + chart immediately without hanging
        const summaryRes = await api.get(
          `/new-simulation/results/${simulationId}/summary`,
          { timeout: 60000 },
        );
        if (!summaryRes.data?.success || summaryRes.data.data == null) {
          setError(
            "Simulation completed but results could not be loaded. Try opening by simulation ID.",
          );
          return;
        }
        const summary = summaryRes.data.data;
        const totalYears = summary.yearlyResults?.length ?? 0;
        setSimulationResults(summary);
        setDetailLoadMeta(
          totalYears > 0
            ? {
                simulationId,
                totalYears,
              }
            : null,
        );

        // Load only lightweight mobilization inputs so mobilization totals are ready immediately.
        setLoadingMobilizationYears(true);
        api
          .get(`/new-simulation/results/${simulationId}/mobilization-years`, {
            timeout: 60000,
          })
          .then((mobilizationRes) => {
            if (mobilizationRes.data?.success && mobilizationRes.data?.data) {
              setMobilizationYearInputs(mobilizationRes.data.data);
            }
          })
          .catch((err) => {
            console.warn("Failed to load mobilization years endpoint:", err);
            setMobilizationYearInputs(null);
          })
          .finally(() => {
            setLoadingMobilizationYears(false);
          });

        setLoading(false);
        setTimeout(() => {
          document
            .getElementById("simulation-results")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } catch (err) {
      console.error("Error running simulation:", err);
      const msg = err.response?.data?.message || err.response?.data?.error;
      setError(msg || "Failed to run simulation");
      const main = document.querySelector("main");
      if (main) main.scrollTo({ top: 0, behavior: "smooth" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
      setActiveRunType(null);
    }
  };

  const handleSaveSnapshot = async () => {
    const trimmedSnapshotTitle = snapshotTitle.trim();

    if (!simulationRecordId) {
      setError("No simulation record available to save.");
      return;
    }

    if (!trimmedSnapshotTitle) {
      setError("Snapshot title is required.");
      return;
    }

    try {
      setSavingSnapshot(true);
      setError(null);
      const response = await api.post(
        `/simulations/${simulationRecordId}/save-snapshot`,
        {
          snapshotTitle: trimmedSnapshotTitle,
          snapshotScope: lastRunSelectAllMode
            ? "full_bridge"
            : "partial_selection",
          sourceSelectAll: lastRunSelectAllMode,
          snapshotSummary: snapshotSummaryPayload,
        },
      );

      if (response.data?.success) {
        setSnapshotSaved(true);
        setShowSaveSnapshotModal(false);
        setSnapshotTitle("");
      } else {
        setError(response.data?.message || "Failed to save snapshot");
      }
    } catch (err) {
      console.error("Error saving snapshot:", err);
      setError(err.response?.data?.message || "Failed to save snapshot");
    } finally {
      setSavingSnapshot(false);
    }
  };

  const openSaveSnapshotModal = () => {
    if (!simulationRecordId) {
      setError("No simulation record available to save.");
      return;
    }

    setError(null);
    setSnapshotTitle("");
    setShowSaveSnapshotModal(true);
  };

  const closeSaveSnapshotModal = () => {
    if (savingSnapshot) return;

    setShowSaveSnapshotModal(false);
    setSnapshotTitle("");
  };

  const renderSaveSnapshotButton = ({ compact = false } = {}) => {
    const isUnavailable = !simulationRecordId;
    const buttonClassName = compact
      ? `inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${snapshotSaved ? "bg-emerald-100 text-emerald-700 cursor-default" : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"}`
      : `flex items-center px-4 py-2 rounded-md transition-colors ${snapshotSaved ? "bg-emerald-100 text-emerald-700 cursor-default" : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"}`;

    return (
      <button
        type="button"
        onClick={openSaveSnapshotModal}
        disabled={isUnavailable || savingSnapshot || snapshotSaved}
        className={buttonClassName}
        title={
          isUnavailable
            ? "Run a simulation before saving a snapshot"
            : "Save this simulation result as a dashboard snapshot"
        }
      >
        <FontAwesomeIcon
          icon={
            snapshotSaved ? faCheckCircle : savingSnapshot ? faSpinner : faSave
          }
          spin={savingSnapshot}
          className={compact ? undefined : "mr-2"}
        />
        {snapshotSaved
          ? "Snapshot Saved"
          : savingSnapshot
            ? "Saving..."
            : "Save Snapshot"}
      </button>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatAuditNumber = (value, digits = 2) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "N/A";
    return numericValue.toLocaleString(undefined, {
      maximumFractionDigits: digits,
    });
  };

  const isAuditResult = Boolean(
    simulationResults?.auditMode || simulationResults?.runType === "audit",
  );

  const auditYearResults = useMemo(() => {
    if (!isAuditResult || !Array.isArray(simulationResults?.yearlyResults)) {
      return [];
    }
    return simulationResults.yearlyResults.filter(
      (yearResult) =>
        yearResult?.auditTrail ||
        (Array.isArray(yearResult?.spanResults) &&
          yearResult.spanResults.some((spanResult) => spanResult?.auditTrail)),
    );
  }, [isAuditResult, simulationResults]);

  const hasAuditTrailRows = auditYearResults.some((yearResult) =>
    Array.isArray(yearResult?.spanResults)
      ? yearResult.spanResults.some((spanResult) => spanResult?.auditTrail)
      : false,
  );

  const renderAuditTrailSection = () => {
    if (!isAuditResult) return null;

    const runAudit = simulationResults?.auditTrail || {};
    const runInputs = runAudit.inputs || runAudit.selection || {};
    const settings = runAudit.settings || runAudit.defaults || {};
    const auditScenario = runAudit.scenario || simulationResults?.scenario || {};
    const isAuditDetailLoading = Boolean(detailLoadMeta || loadingDetailProgress);
    const cycleLimitSummary = Object.entries(
      settings.maintenanceCycleLimitsByCategory || {},
    )
      .map(([category, limits]) => {
        const touchupMax = formatAuditNumber(limits?.touchupMax, 0);
        const overcoatMax = formatAuditNumber(limits?.overcoatMax, 0);
        return `${category}: TU ${touchupMax}, OC ${overcoatMax}`;
      })
      .join("; ");

    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 space-y-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon icon={faSearch} className="text-indigo-600" />
                Audit Trail
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedSpanIds.length.toLocaleString()} {totalAreaDataEntityLabel.toLowerCase()} selected
              </p>
            </div>
            <span className="inline-flex items-center rounded-md bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700">
              Manual audit run
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Run Inputs
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Elements</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(
                      runInputs.selectedElementCount ??
                        runInputs.simulatedElementCount ??
                        selectedSpanIds.length,
                      0,
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Years</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(
                      runInputs.timeHorizonYears ??
                        auditScenario.timeHorizonYears ??
                        simulationResults?.timeHorizonYears,
                      0,
                    )}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Start Year</dt>
                  <dd className="font-medium text-gray-900">
                    {runInputs.simulationStartYear ||
                      settings.simulationStartYear ||
                      simulationResults?.simulationStartYear ||
                      "N/A"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Inflation</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(
                      (runInputs.discountRate ??
                        auditScenario.discountRate ??
                        simulationResults?.summary?.calculationLog
                          ?.inflationRate) * 100,
                    )}
                    %
                  </dd>
                </div>
              </dl>
            </div>

            <div className="border rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Defaults Loaded
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Initial Degradation</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(settings.defaultInitialDegradationPercent)}%
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Reset Baseline</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(settings.degradationResetBaseline)}%
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Cycle Limits</dt>
                  <dd className="font-medium text-gray-900">
                    {cycleLimitSummary || "Configured by category"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Max Wash-only</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(settings.maxConsecutiveWashOnly, 0)}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="border rounded-md p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Mobilization
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Total</dt>
                  <dd className="font-medium text-gray-900">
                    {formatCurrency(runAudit.mobilization?.totalCost || 0)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Years With Cost</dt>
                  <dd className="font-medium text-gray-900">
                    {formatAuditNumber(runAudit.mobilization?.yearlyCostCount, 0)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {isAuditDetailLoading && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading audit years
              {loadingDetailProgress
                ? ` (${loadingDetailProgress.loaded}/${loadingDetailProgress.total})`
                : ""}
            </div>
          )}

          <div className="space-y-3">
            {auditYearResults.map((yearResult, yearIndex) => {
              const auditRows = Array.isArray(yearResult.spanResults)
                ? yearResult.spanResults.filter((spanResult) => spanResult?.auditTrail)
                : [];
              const yearAudit = yearResult.auditTrail || {};

              return (
                <details
                  key={`${yearResult.year || yearIndex}-audit`}
                  className="border rounded-md bg-white"
                  open={yearIndex === 0}
                >
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-900 flex items-center justify-between gap-3">
                    <span>
                      Year {yearResult.year || yearIndex + 1}
                      {yearResult.calendarYear ? ` (${yearResult.calendarYear})` : ""}
                    </span>
                    <span className="text-xs font-medium text-gray-500">
                      {formatAuditNumber(auditRows.length, 0)} audited rows - {formatCurrency(yearResult.totalCost || 0)}
                    </span>
                  </summary>
                  <div className="border-t p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-md p-3">
                        <div className="text-gray-500">Evaluated</div>
                        <div className="font-semibold text-gray-900">
                          {formatAuditNumber(yearAudit.evaluatedElementCount ?? auditRows.length, 0)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-md p-3">
                        <div className="text-gray-500">Repaired</div>
                        <div className="font-semibold text-gray-900">
                          {formatAuditNumber(yearAudit.repairedElementCount ?? yearResult.repairedSpanCount, 0)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-md p-3">
                        <div className="text-gray-500">Wash-only</div>
                        <div className="font-semibold text-gray-900">
                          {formatAuditNumber(yearAudit.washOnlyElementCount, 0)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-md p-3">
                        <div className="text-gray-500">Mobilization</div>
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(yearAudit.mobilization?.totalCost || yearResult.mobilizationCost || 0)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 max-h-[36rem] overflow-y-auto pr-1">
                      {auditRows.map((spanResult) => {
                        const audit = spanResult.auditTrail || {};
                        const element = audit.element || {};
                        const decision = audit.repairDecision || {};
                        const degradation = audit.degradation || {};
                        const costs = audit.costs || {};
                        const auditPayloadJson = JSON.stringify(audit, null, 2);

                        return (
                          <details
                            key={`${yearResult.year}-${spanResult.spanId}`}
                            className="border rounded-md bg-gray-50"
                          >
                            <summary className="cursor-pointer px-4 py-3 text-sm flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <span className="font-medium text-gray-900">
                                {element.name ||
                                  spanResult.spanName ||
                                  element.code ||
                                  spanResult.spanCode ||
                                  `Element ${spanResult.spanId}`}
                              </span>
                              <span className="flex flex-wrap gap-2 text-xs text-gray-600">
                                <span className="rounded bg-white border px-2 py-1">
                                  Degradation {formatAuditNumber(degradation.resultPercent)}%
                                </span>
                                <span className="rounded bg-white border px-2 py-1">
                                  Decision {decision.finalLabel || decision.rawDecision || "None"}
                                </span>
                                {(spanResult.triggeringPortions?.length || 0) >
                                  0 && (
                                  <span className="rounded bg-amber-100 border border-amber-300 text-amber-900 px-2 py-1">
                                    Driven by{" "}
                                    {spanResult.triggeringPortions.join(", ")}
                                  </span>
                                )}
                                <span className="rounded bg-white border px-2 py-1">
                                  Total {formatCurrency(spanResult.totalCost || 0)}
                                </span>
                              </span>
                            </summary>
                            <div className="border-t p-4 space-y-4 text-sm">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">Element</h4>
                                  <dl className="space-y-1 text-gray-600">
                                    <div>Area: {formatAuditNumber(element.totalArea)} m²</div>
                                    <div>Combination: {element.combinationCode || "N/A"}</div>
                                    <div>Category: {element.corrosionCategory || "N/A"}</div>
                                    <div>Position: {element.position || "N/A"}</div>
                                  </dl>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">Decision</h4>
                                  <dl className="space-y-1 text-gray-600">
                                    <div>Source: {audit.trigger?.source || "N/A"}</div>
                                    <div>Raw: {decision.rawDecision || "None"}</div>
                                    <div>Final: {decision.finalLabel || "None"}</div>
                                    <div>Split: {audit.damageSplit?.source || "N/A"}</div>
                                  </dl>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">Cost</h4>
                                  <dl className="space-y-1 text-gray-600">
                                    <div>Repair: {formatCurrency(costs.repairSubtotal || 0)}</div>
                                    <div>Wash: {formatCurrency(spanResult.costWash || 0)}</div>
                                    <div>Spring Wash: {formatCurrency(spanResult.springWashCost || 0)}</div>
                                    <div>Total: {formatCurrency(spanResult.totalCost || 0)}</div>
                                  </dl>
                                </div>
                              </div>

                              <DamagePortionsBreakdown
                                portions={spanResult.portionStates}
                              />

                              {Array.isArray(decision.steps) && decision.steps.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2">Decision Steps</h4>
                                  <ul className="list-disc ml-5 text-gray-600 space-y-1">
                                    {decision.steps.map((step, stepIndex) => (
                                      <li key={stepIndex}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <details className="overflow-hidden rounded-md border border-gray-200 bg-white">
                                <summary className="cursor-pointer select-none bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100">
                                  Payload JSON
                                </summary>
                                <pre className="m-0 max-h-72 overflow-auto whitespace-pre-wrap break-words bg-slate-50 p-3 font-mono text-[11px] leading-5 text-slate-700">
                                  <code>{auditPayloadJson}</code>
                                </pre>
                              </details>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                </details>
              );
            })}
          </div>

          {!isAuditDetailLoading && auditYearResults.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Audit metadata was saved, but detailed year payloads have not been loaded.
            </div>
          )}
        </div>
      </div>
    );
  };

  const mobilizationProductivityMatrixByYear = useMemo(() => {
    const matrix = [];
    (mobilizationMasterYears.productivityYears || []).forEach((group) => {
      const map = new Map();
      (group.standards || []).forEach((row) => {
        const steel = row.steelSize || row.steel_size || "";
        const coat = row.coatType || row.coat_type || "";
        const speed = row.speedBand || row.speed_band || "";
        const method = String(
          row.accessMethod || row.access_method || "",
        ).toLowerCase();
        const key = `${group.year}|${steel}|${coat}|${speed}`;

        if (!map.has(key)) {
          map.set(key, {
            key,
            year: Number(group.year),
            steel_size: steel,
            coat_type: coat,
            speed_band: speed,
            sqft: { brush: 0, roll: 0, spray: 0 },
          });
        }

        const item = map.get(key);
        if (["brush", "roll", "spray"].includes(method)) {
          item.sqft[method] = Number(row.sqftPerHour || row.sqft_per_hour || 0);
        }
      });

      matrix.push(...Array.from(map.values()));
    });

    return matrix.sort((a, b) => Number(b.year) - Number(a.year));
  }, [mobilizationMasterYears.productivityYears]);

  const mobilizationSummary = useMemo(() => {
    const lightweightYears = Array.isArray(mobilizationYearInputs?.years)
      ? mobilizationYearInputs.years
      : [];
    const fallbackYears = Array.isArray(simulationResults?.yearlyResults)
      ? simulationResults.yearlyResults
      : [];
    const sourceYearRows =
      lightweightYears.length > 0 ? lightweightYears : fallbackYears;

    if (!sourceYearRows.length) {
      return { yearlyCosts: {}, totalCost: 0 };
    }

    const simulationStartYear =
      mobilizationYearInputs?.simulationStartYear != null &&
      mobilizationYearInputs?.simulationStartYear > 0
        ? Number(mobilizationYearInputs.simulationStartYear)
        : simulationResults?.simulationStartYear != null &&
            simulationResults?.simulationStartYear > 0
          ? simulationResults.simulationStartYear
          : new Date().getFullYear();

    const hasPersistedMobilization = sourceYearRows.some(
      (row) =>
        row?.mobilizationDetail != null ||
        Number(row?.mobilizationCost || row?.mobilization_cost || 0) > 0,
    );

    if (hasPersistedMobilization) {
      const yearlyCosts = {};
      sourceYearRows.forEach((yearResult, idx) => {
        const yearIndex = Number(
          yearResult.year || yearResult.yearIndex || idx + 1,
        );
        const calendarYear =
          Number(yearResult.calendarYear) ||
          simulationStartYear + yearIndex - 1;
        const detail = yearResult.mobilizationDetail || null;

        if (detail && typeof detail === "object") {
          yearlyCosts[calendarYear] = {
            ...detail,
            year: Number(detail.year || calendarYear),
            totalCost: Number(detail.totalCost || 0),
          };
          return;
        }

        yearlyCosts[calendarYear] = {
          costType: "mobilization",
          year: calendarYear,
          totalCost: Number(
            yearResult.mobilizationCost || yearResult.mobilization_cost || 0,
          ),
          totalArea: Number(
            yearResult.totalRepairDegradedArea ||
              yearResult.total_repair_degraded_area ||
              0,
          ),
          repairsCount: Number(
            yearResult.repairedSpanCount || yearResult.repaired_span_count || 0,
          ),
        };
      });

      const totalCost = Object.values(yearlyCosts).reduce(
        (sum, row) => sum + Number(row.totalCost || 0),
        0,
      );

      return { yearlyCosts, totalCost };
    }

    const toNum = (value) => Number(value || 0);
    const toInt = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const normalizeApplicationType = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();
    const normalizeSteelType = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const getCostsForYear = (calendarYear) => {
      const year = Number(calendarYear);
      const sorted = [...(mobilizationMasterYears.generalCostYears || [])].sort(
        (a, b) => Number(b.year) - Number(a.year),
      );
      const hit = sorted.find((group) => Number(group.year) <= year);
      return hit?.costs || [];
    };

    const yearlyCosts = {};
    const productivityMethodAdjustmentMap = buildProductivityMethodAdjustmentMap(
      mobilizationMasterYears.productivityMethodAdjustmentSets,
    );

    const getMobilizationConfigForYear = (calendarYear) => {
      const year = Number(calendarYear);
      const groups = [
        ...(mobilizationMasterYears.mobilizationConfigYears || []),
      ].sort((a, b) => Number(b.year) - Number(a.year));

      const nearestPastOrCurrent = groups.find(
        (group) => Number(group.year) <= year,
      );
      if (nearestPastOrCurrent) {
        return (
          (nearestPastOrCurrent.configs || []).find((c) =>
            Boolean(c.isActive || c.is_active),
          ) || (nearestPastOrCurrent.configs || [])[0]
        );
      }

      return null;
    };

    const parseSelectedIds = (rawValue) => {
      if (Array.isArray(rawValue)) {
        return rawValue
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0);
      }

      if (typeof rawValue === "string" && rawValue.trim()) {
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            return parsed
              .map((id) => Number(id))
              .filter((id) => Number.isInteger(id) && id > 0);
          }
        } catch (err) {
          return [];
        }
      }

      return [];
    };

    sourceYearRows.forEach((yearResult, idx) => {
      const yearIndex = Number(
        yearResult.year || yearResult.yearIndex || idx + 1,
      );
      const calendarYear =
        Number(yearResult.calendarYear) || simulationStartYear + yearIndex - 1;
      const spanResults = Array.isArray(yearResult.spanResults)
        ? yearResult.spanResults
        : [];
      const summaryRepairsCount = toInt(
        yearResult.repairedSpanCount || yearResult.repaired_span_count,
      );
      const summaryDegradedArea = toNum(
        yearResult.totalRepairDegradedArea ||
          yearResult.total_repair_degraded_area,
      );

      const repairedSpanResults =
        summaryRepairsCount > 0 || summaryDegradedArea > 0
          ? []
          : spanResults.filter((sr) => Number(sr.totalCost || 0) > 0);

      const totalDegradedArea =
        summaryDegradedArea > 0
          ? summaryDegradedArea
          : repairedSpanResults.reduce((sum, sr) => {
              const directDegradedArea = toNum(
                sr.degradedArea || sr.degraded_area,
              );
              if (directDegradedArea > 0) return sum + directDegradedArea;

              const areaFromSplits =
                toNum(sr.areaA) + toNum(sr.areaB) + toNum(sr.areaC);
              if (areaFromSplits > 0) return sum + areaFromSplits;

              return sum;
            }, 0);

      const repairsCount =
        summaryRepairsCount > 0
          ? summaryRepairsCount
          : summaryDegradedArea > 0
            ? 1
            : repairedSpanResults.length;

      if (totalDegradedArea <= 0) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: 0,
          repairsCount,
        };
        return;
      }

      const selectedConfig = getMobilizationConfigForYear(calendarYear);

      if (!selectedConfig) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: "No mobilization configuration found for year",
        };
        return;
      }

      const teamId = Number(
        selectedConfig.teamId ||
          selectedConfig.team_id ||
          selectedConfig.teamConfig?.id ||
          0,
      );

      let selectedTeamConfig = null;
      for (const group of mobilizationMasterYears.teamYears || []) {
        const team = (group.teams || []).find(
          (item) => Number(item.id) === Number(teamId),
        );
        if (team) {
          selectedTeamConfig = team;
          break;
        }
      }

      if (!selectedTeamConfig) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: "Selected mobilization team config not found",
        };
        return;
      }

      const targetYear = Number(calendarYear);
      const steelType = String(
        selectedConfig.steelType || selectedConfig.steel_type || "",
      ).toLowerCase();
      const coatType = String(
        selectedConfig.coatType || selectedConfig.coat_type || "",
      ).toLowerCase();
      const speedBand = String(
        selectedConfig.speedBand || selectedConfig.speed_band || "",
      ).toLowerCase();
      const groupedDegradedAreasRaw = Array.isArray(
        yearResult.groupedDegradedAreas,
      )
        ? yearResult.groupedDegradedAreas
        : [];

      const groupedDegradedAreas = groupedDegradedAreasRaw
        .map((group) => ({
          applicationType: normalizeApplicationType(group.applicationType),
          accessMethod: group.accessMethod || null,
          steelType: normalizeSteelType(group.steelType),
          combinationCode: normalizeDamageCombinationCode(
            group.combinationCode ||
              group.damageCombinationCode ||
              group.corrosionDamageCombinationCode,
          ),
          exposureClassCode: normalizeExposureClassCode(
            group.exposureClassCode || group.corrosionCategory,
          ),
          degradedArea: toNum(
            group.totalDegradedArea ||
              group.degradedArea ||
              group.total_degraded_area,
          ),
        }))
        .filter(
          (group) =>
            group.applicationType && group.steelType && group.degradedArea > 0,
        );

      if (!groupedDegradedAreas.length) {
        groupedDegradedAreas.push({
          applicationType: normalizeApplicationType(
            selectedConfig.applicationType || selectedConfig.application_type,
          ),
          accessMethod: null,
          steelType,
          degradedArea: totalDegradedArea,
        });
      }

      let requiredManhours = 0;
      const missingProductivityGroups = [];
      const zeroAdjustedProductivityGroups = [];

      groupedDegradedAreas.forEach((group) => {
        const productivityCandidates = mobilizationProductivityMatrixByYear
          .filter(
            (row) =>
              Number(row.year) <= targetYear &&
              String(row.steel_size || "").toLowerCase() === group.steelType &&
              String(row.coat_type || "").toLowerCase() === coatType &&
              String(row.speed_band || "").toLowerCase() === speedBand,
          )
          .sort((a, b) => Number(b.year) - Number(a.year));

        const productivity = productivityCandidates[0] || null;
        const rawSqftPerHour = toNum(
          productivity?.sqft?.[group.applicationType],
        );
        const productivityAdjustmentPercent =
          getProductivityMethodAdjustmentPercent(
            productivityMethodAdjustmentMap,
            group.combinationCode,
            group.accessMethod,
          );
        const sqftPerHour =
          rawSqftPerHour * (productivityAdjustmentPercent / 100);
        const groupLabel = `${group.applicationType || "unknown"}${group.accessMethod ? `/${group.accessMethod}` : ""}/${group.steelType || "unknown"}${
          group.combinationCode
            ? `/${group.combinationCode}`
            : group.exposureClassCode
              ? `/${group.exposureClassCode}`
              : ""
        }`;
        const sqmPerHour = sqftPerHour * 0.09290304;
        const manhoursPerSqm = sqmPerHour > 0 ? 1 / sqmPerHour : 0;

        if (!productivity || !(rawSqftPerHour > 0)) {
          missingProductivityGroups.push(groupLabel);
          return;
        }

        if (!manhoursPerSqm) {
          zeroAdjustedProductivityGroups.push(
            `${groupLabel} (${productivityAdjustmentPercent}% adjustment)`,
          );
          return;
        }

        requiredManhours += group.degradedArea * manhoursPerSqm;
      });

      if (missingProductivityGroups.length > 0) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: `No matching productivity row found for: ${missingProductivityGroups.join(", ")}`,
        };
        return;
      }

      if (zeroAdjustedProductivityGroups.length > 0) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: `Productivity adjustment reduced productivity to zero for: ${zeroAdjustedProductivityGroups.join(", ")}`,
        };
        return;
      }

      if (!(requiredManhours > 0)) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: "Required manhours could not be calculated from element data",
        };
        return;
      }

      const allocations =
        selectedTeamConfig.jobAllocations ||
        selectedTeamConfig.job_allocations ||
        [];

      const {
        teamPainterCount,
        teamPaintingHoursPerWeek,
        perTeamForemanPaintingHoursPerWeek,
        perTeamNonForemanPaintingHoursPerWeek,
        teamWeeklyLaborCost,
        perTeamForemanWeeklyCost,
        perTeamNonForemanWeeklyCost,
      } = calculateTeamWeeklyMetrics(allocations);

      if (teamPaintingHoursPerWeek <= 0) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: "Team painting capacity is zero",
        };
        return;
      }

      const weeksLimit =
        Number(scenario?.mobilizationWeeksLimit) > 0
          ? Number(scenario.mobilizationWeeksLimit)
          : 35;
      const calendarConstraint = calculateCalendarConstraintWeeks(
        spanResults,
        scenario?.layersPerDay,
      );
      const durationResult = resolveTeamsAndWeeks({
        requiredManhours,
        weekLimit: weeksLimit,
        foremansLimit: scenario?.foremansCountLimit,
        perTeamNonForemanPaintingHoursPerWeek,
        perTeamForemanPaintingHoursPerWeek,
        calendarWeeksLowerBound: calendarConstraint.weeksLowerBound,
      });

      if (durationResult.error) {
        yearlyCosts[calendarYear] = {
          totalCost: 0,
          totalArea: totalDegradedArea,
          repairsCount,
          error: durationResult.error,
        };
        return;
      }

      const {
        teamsRequired,
        weeksRequired,
        laborWeeksRequired,
        calendarWeeksLowerBound,
        foremansLimit: foremansCountLimit,
      } = durationResult;

      const teamsWithForemanCost =
        perTeamForemanWeeklyCost > 0
          ? Math.min(teamsRequired, foremansCountLimit)
          : 0;

      const allGeneralCosts = getCostsForYear(calendarYear);
      const selectedIds = parseSelectedIds(
        selectedConfig.selectedGeneralCostIds ||
          selectedConfig.selected_general_cost_ids ||
          [],
      );
      const selectedGeneralCosts =
        selectedIds.length > 0
          ? allGeneralCosts.filter((row) =>
              selectedIds.includes(Number(row.id)),
            )
          : allGeneralCosts;

      const generalCostBreakdown = selectedGeneralCosts.map((row) => {
        const convertedTotal = calculateGeneralCostBaseTotal({
          data: row,
          weeksRequired,
          teamsRequired,
        });

        return {
          id: row.id,
          name: row.costCategory || row.cost_category || "General Cost",
          convertedTotal,
        };
      });

      const generalCostsTotal = generalCostBreakdown.reduce(
        (sum, row) => sum + Number(row.convertedTotal || 0),
        0,
      );
      const teamLaborTotal =
        (perTeamNonForemanWeeklyCost * teamsRequired +
          perTeamForemanWeeklyCost * teamsWithForemanCost) *
        weeksRequired;
      const totalCost = teamLaborTotal + generalCostsTotal;

      yearlyCosts[calendarYear] = {
        costType: "mobilization",
        year: calendarYear,
        totalCost,
        totalArea: totalDegradedArea,
        repairsCount,
        requiredManhours,
        teamPainterCount,
        teamPaintingHoursPerWeek,
        teamWeeklyLaborCost,
        teamsRequired,
        foremansCountLimit,
        teamsWithForemanCost,
        weeksRequired,
        laborWeeksRequired,
        calendarWeeksLowerBound,
        calendarDaysLowerBound: Number(
          calendarConstraint.maxElementCalendarDays || 0,
        ),
        layersPerDay: Number(calendarConstraint.layersPerDay || 0),
        maxElementEffectiveLayers: Number(
          calendarConstraint.maxElementEffectiveLayers || 0,
        ),
        constrainedElementCount: Number(
          calendarConstraint.constrainedElementCount || 0,
        ),
        weekLimit: weeksLimit,
        teamLaborTotal,
        generalCostsTotal,
        generalCostBreakdown,
        configSummary: {
          teamName:
            selectedConfig?.teamConfig?.teamName ||
            selectedConfig?.teamConfig?.team_name ||
            "Team",
          applicationType: "From element data",
          speedBand: selectedConfig.speedBand || selectedConfig.speed_band,
          coatType: selectedConfig.coatType || selectedConfig.coat_type,
          steelType: "From element data",
          elementGroups: groupedDegradedAreas,
        },
      };
    });

    const totalCost = Object.values(yearlyCosts).reduce(
      (sum, row) => sum + Number(row.totalCost || 0),
      0,
    );

    return { yearlyCosts, totalCost };
  }, [
    scenario?.foremansCountLimit,
    scenario?.layersPerDay,
    scenario?.mobilizationWeeksLimit,
    mobilizationYearInputs,
    mobilizationMasterYears.generalCostYears,
    mobilizationMasterYears.mobilizationConfigYears,
    mobilizationMasterYears.productivityMethodAdjustmentSets,
    mobilizationMasterYears.teamYears,
    mobilizationProductivityMatrixByYear,
    simulationResults,
  ]);

  const totalCostWithMobilization =
    Number(simulationResults?.summary?.totalCost || 0) +
    Number(mobilizationSummary.totalCost || 0);

  const snapshotTitlePlaceholder =
    selectedBridge?.name ||
    selectedBridge?.bridge_name ||
    "Enter snapshot title";

  const snapshotSummaryPayload = useMemo(() => {
    if (!simulationResults) return null;

    const yearlyResults = Array.isArray(simulationResults?.yearlyResults)
      ? simulationResults.yearlyResults
      : [];
    const simulationStartYear = Number(
      simulationResults?.scenario?.currentYear || new Date().getFullYear(),
    );

    const yearlyTotals = yearlyResults.map((yearResult, idx) => {
      const yearIndex = Number(yearResult?.year || idx + 1);
      const calendarYear = simulationStartYear + yearIndex - 1;
      const repairCost = Number(yearResult?.totalCost || 0);
      const mobilizationCost = Number(
        mobilizationSummary?.yearlyCosts?.[calendarYear]?.totalCost || 0,
      );

      return {
        yearIndex,
        calendarYear,
        totalCost: repairCost + mobilizationCost,
        repairCost,
        mobilizationCost,
      };
    });

    const totalCost = yearlyTotals.reduce(
      (sum, row) => sum + Number(row.totalCost || 0),
      0,
    );
    const avgYearlyCost =
      yearlyTotals.length > 0 ? totalCost / yearlyTotals.length : 0;

    let peakCost = 0;
    let peakCalendarYear = null;
    yearlyTotals.forEach((row) => {
      const value = Number(row.totalCost || 0);
      if (value > peakCost) {
        peakCost = value;
        peakCalendarYear = row.calendarYear;
      }
    });

    return {
      totalCost,
      avgYearlyCost,
      peakCost,
      peakCostYear: {
        year: peakCalendarYear,
        cost: peakCost,
      },
      totalMobilizationCost: Number(mobilizationSummary?.totalCost || 0),
      yearlyTotals,
    };
  }, [mobilizationSummary, simulationResults]);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Note: Pagination removed - using hierarchical selection instead

  const buildSimpleHierarchyFallback = useCallback(
    (results, bridgeContext = {}) => {
      if (!results) return null;

      const spans = Array.isArray(results.spans) ? results.spans : [];
      const yearlyResults = Array.isArray(results.yearlyResults)
        ? results.yearlyResults
        : [];
      if (!spans.length || !yearlyResults.length) return null;

      const toPositiveInt = (value) => {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
      };

      const startYear =
        results.simulationStartYear != null && results.simulationStartYear > 0
          ? results.simulationStartYear
          : new Date().getFullYear();

      const allYears = yearlyResults.map((yearRow, idx) => {
        const yearIndex = Number(yearRow?.year || idx + 1);
        return startYear + yearIndex - 1;
      });

      const spanNodes = spans.map((span) => {
        const spanId =
          toPositiveInt(span?.id) ||
          toPositiveInt(span?.spanId) ||
          toPositiveInt(span?.span_id) ||
          toPositiveInt(span?.elementId) ||
          toPositiveInt(span?.element_id);

        const node = {
          id: spanId,
          type: "span",
          name:
            span?.spanName ||
            span?.name ||
            span?.spanCode ||
            span?.code ||
            "Span",
          code: span?.spanCode || span?.code || "",
          area: String(Number(span?.totalArea || span?.total_area || 0) || 0),
          description: span?.spanName || span?.name || "Span",
          level: 1,
          levelLabel: "Span",
          children: [],
          years: allYears.map((year) => ({
            year,
            cost: 0,
            degradationPercent: 0,
          })),
        };

        yearlyResults.forEach((yearRow, idx) => {
          const spanResults = Array.isArray(yearRow?.spanResults)
            ? yearRow.spanResults
            : [];
          if (!spanId || !spanResults.length) return;

          const match = spanResults.find((sr) => {
            const srId =
              toPositiveInt(sr?.spanId) ||
              toPositiveInt(sr?.span_id) ||
              toPositiveInt(sr?.elementId) ||
              toPositiveInt(sr?.element_id);
            return srId === spanId;
          });
          if (!match || !node.years[idx]) return;

          node.years[idx].cost = Number(match.totalCost || 0);
          node.years[idx].degradationPercent = Number(
            match.degradationPercent || 0,
          );
        });

        return node;
      });

      const bridgeNode = {
        type: "bridge",
        name:
          selectedBridge?.name ||
          bridgeContext?.bridgeName ||
          `Bridge ${bridgeContext?.bridgeId || ""}`.trim(),
        code: selectedBridge?.bridgeCode || bridgeContext?.bridgeCode || "",
        area: spanNodes
          .reduce((sum, node) => sum + (Number(node.area) || 0), 0)
          .toString(),
        description: "Main bridge structure",
        children: spanNodes,
        years: allYears.map((year, idx) => ({
          year,
          cost: spanNodes.reduce(
            (sum, node) => sum + (Number(node?.years?.[idx]?.cost || 0) || 0),
            0,
          ),
          degradationPercent: 0,
        })),
      };

      return bridgeNode;
    },
    [selectedBridge],
  );

  // Transform simulation results into hierarchy format for accordion display
  const transformToHierarchyFn = async (results) => {
    if (!results) return null;

    // For Monte Carlo, we don't show the hierarchical table (use Monte Carlo component instead)
    if (results.mode === "monte_carlo") {
      return null;
    }

    const spans = results.spans;
    const yearlyResults = results.yearlyResults;

    if (!yearlyResults || !spans || spans.length === 0) return null;

    const toPositiveInt = (value) => {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    };

    // Use selected bridge context when available; otherwise infer from result payload.
    const bridgeId =
      toPositiveInt(selectedBridgeId) ||
      toPositiveInt(results.bridgeId) ||
      toPositiveInt(results.bridge_id) ||
      toPositiveInt(results?.scenario?.bridgeId) ||
      toPositiveInt(results?.scenario?.bridge_id) ||
      toPositiveInt(selectedBridge?.id) ||
      toPositiveInt(spans[0]?.bridgeId) ||
      toPositiveInt(spans[0]?.bridge_id) ||
      null;

    if (!bridgeId) {
      console.warn(
        "transformToHierarchy: could not resolve bridgeId; using simple fallback hierarchy",
      );
      return buildSimpleHierarchyFallback(results);
    }

    const levelConfig = selectedBridge?.levelLabels
      ? typeof selectedBridge.levelLabels === "string"
        ? JSON.parse(selectedBridge.levelLabels)
        : selectedBridge.levelLabels
      : {};

    // Fetch hierarchy structure from backend
    try {
      const elementIds = Array.from(
        new Set(
          spans
            .map(
              (s) =>
                toPositiveInt(s?.id) ||
                toPositiveInt(s?.spanId) ||
                toPositiveInt(s?.span_id) ||
                toPositiveInt(s?.elementId) ||
                toPositiveInt(s?.element_id),
            )
            .filter(Boolean),
        ),
      );

      if (elementIds.length === 0) {
        console.warn(
          "transformToHierarchy: no valid element IDs found in spans; using simple fallback hierarchy",
        );
        return buildSimpleHierarchyFallback(results, { bridgeId });
      }

      const hierarchyResponse = await api.post(
        "/new-simulation/hierarchy-structure",
        {
          elementIds: elementIds,
          bridgeId: bridgeId,
        },
      );

      if (!hierarchyResponse.data.success) {
        console.error("Failed to fetch hierarchy structure");
        return buildSimpleHierarchyFallback(results, { bridgeId });
      }

      const { hierarchy, levelConfig: fetchedLevelConfig } =
        hierarchyResponse.data.data;
      const finalLevelConfig = fetchedLevelConfig || levelConfig;

      // Use simulation start year (Default Year 1 from settings) or current year
      const startYear =
        results.simulationStartYear != null && results.simulationStartYear > 0
          ? results.simulationStartYear
          : new Date().getFullYear();
      const timeHorizon = results.timeHorizonYears || 30;
      const allYears = [];
      for (let i = 1; i <= timeHorizon; i++) {
        allYears.push(startYear + i - 1);
      }

      // Build fast lookup indexes once. This avoids repeated O(n) scans per node/year.
      const spanByElementId = new Map();
      (spans || []).forEach((span) => {
        const elementId =
          toPositiveInt(span?.id) ||
          toPositiveInt(span?.spanId) ||
          toPositiveInt(span?.span_id) ||
          toPositiveInt(span?.elementId) ||
          toPositiveInt(span?.element_id);
        if (!elementId) return;
        if (!spanByElementId.has(elementId)) {
          spanByElementId.set(elementId, span);
        }
      });

      const spanResultsByElementId = new Map();
      (yearlyResults || []).forEach((yearResult, yearIdx) => {
        (yearResult?.spanResults || []).forEach((spanResult) => {
          const elementId =
            toPositiveInt(spanResult?.spanId) ||
            toPositiveInt(spanResult?.span_id) ||
            toPositiveInt(spanResult?.elementId) ||
            toPositiveInt(spanResult?.element_id);
          if (!elementId) return;

          let byYear = spanResultsByElementId.get(elementId);
          if (!byYear) {
            byYear = [];
            spanResultsByElementId.set(elementId, byYear);
          }
          byYear[yearIdx] = spanResult;
        });
      });

      // Build hierarchy tree with costs
      const buildHierarchyWithCosts = (elements, parentLevel = 0) => {
        return elements.map((element) => {
          const levelLabel =
            finalLevelConfig[`level${element.levelNumber}`]?.label ||
            `Level ${element.levelNumber}`;

          // Prefer DB element name for output clarity; fallback to code.
          let displayName = element.name || element.code || "Unnamed";

          // Get area from element, defaulting to 0 if not available
          const elementArea =
            parseFloat(element.area || element.total_area || 0) || 0;

          const node = {
            id: element.id, // Store element ID for span result lookup
            type:
              element.levelNumber === 1
                ? "bridge"
                : element.levelNumber === 2
                  ? "span"
                  : element.levelNumber === 3
                    ? "panelbay"
                    : "asset",
            name: displayName,
            code: element.code || "",
            area: elementArea.toString(),
            description: `${levelLabel}: ${displayName}`,
            level: element.levelNumber,
            levelLabel: levelLabel,
            children: [],
            years: [],
          };

          // Initialize years array with all years (set to 0 initially)
          node.years = allYears.map((year) => ({
            year,
            cost: 0,
            degradationPercent: 0,
          }));

          // Check if this element has structural children (will be determined after building children)
          // We'll set this flag after building children to decide whether to use own cost or sum of children
          // IMPORTANT: Only set own cost for nodes that are at the area data level
          // This cost will be REPLACED with sum of children if element has structural children
          // Only leaf nodes (no structural children) should keep their own span cost
          const elementId = toPositiveInt(element.id);
          const matchingSpan = elementId
            ? spanByElementId.get(elementId)
            : null;
          if (matchingSpan) {
            // Add yearly costs for this element (temporarily, will be replaced if has children)
            const spanResultsByYear =
              (elementId && spanResultsByElementId.get(elementId)) || [];

            // A split element has no single exposure class, and its element-level
            // degradation is only the dominant portion's. Label it with every
            // portion and carry each portion's own curve onto the year cells.
            const portionShape = spanResultsByYear.find(
              (row) => Array.isArray(row?.portionStates) && row.portionStates.length > 1,
            )?.portionStates;

            const exposureCategory =
              matchingSpan.corrosionCategory ||
              matchingSpan.corrosion_category ||
              null;
            const exposureLabel = portionShape
              ? portionShape
                  .map(
                    (portion) =>
                      `${portion.combinationCode}${
                        Number(portion.percentage) > 0
                          ? ` ${Number(portion.percentage).toFixed(0)}%`
                          : ""
                      }`,
                  )
                  .join(" · ")
              : exposureCategory;
            if (exposureLabel) {
              node.description = `${levelLabel}: ${displayName} | Exposure: ${exposureLabel}`;
            }

            for (let yearIdx = 0; yearIdx < node.years.length; yearIdx += 1) {
              const spanResult = spanResultsByYear[yearIdx];
              if (!spanResult) continue;

              node.years[yearIdx].cost = Number(spanResult.totalCost) || 0;
              node.years[yearIdx].degradationPercent =
                Number(spanResult.degradationPercent) || 0;
              if (
                Array.isArray(spanResult.portionStates) &&
                spanResult.portionStates.length > 1
              ) {
                node.years[yearIdx].portionDegradations =
                  spanResult.portionStates.map((portion) => ({
                    combinationCode: portion.combinationCode,
                    category: portion.category,
                    degradationPercent: Number(portion.degradationPercent) || 0,
                    triggered: Boolean(portion.triggered),
                  }));
              }
            }

            // Add repair types directly under the area-data node (no intermediate Paint row).
            const repairTypeRows = [
              {
                key: "SPRING_WASH",
                label: "Spring Wash/Rinse",
                costKey: "springWashCost",
              },
              { key: "WASH", label: "Wash", costKey: "independentWashCost" },
              { key: "TU", label: "Touch-up", costKey: "costTU" },
              { key: "OC", label: "Overcoat", costKey: "costOC" },
              { key: "RE", label: "Recoat", costKey: "costRE" },
            ];

            repairTypeRows.forEach(({ key, label, costKey }) => {
              const activityDescriptionByKey = {
                SPRING_WASH: "Spring wash/rinse (separate from paint jobs)",
                WASH: "Independent/default wash-only cycle (non-spring)",
                TU: "Touch-up (includes default wash when paint job runs)",
                OC: "Overcoat (includes default wash when paint job runs)",
                RE: "Recoat (includes default wash when paint job runs)",
              };

              const activityNode = {
                id: element.id,
                type: "activity",
                name: label,
                description:
                  activityDescriptionByKey[key] ||
                  `${label} (cost only in years when this repair runs)`,
                repairTypeKey: key,
                years: [],
              };
              activityNode.years = allYears.map((year) => ({
                year,
                cost: 0,
                degradationPercent: 0,
              }));

              const spanResultsByYear =
                (elementId && spanResultsByElementId.get(elementId)) || [];

              for (
                let yearIdx = 0;
                yearIdx < activityNode.years.length;
                yearIdx += 1
              ) {
                const spanResult = spanResultsByYear[yearIdx];
                if (!spanResult) continue;

                const repairLabel = String(
                  spanResult.repairLabel || "",
                ).toUpperCase();
                let cost = 0;

                if (key === "SPRING_WASH") {
                  // Spring wash/rinse is always separate from paint rows.
                  cost = Number(spanResult.springWashCost) || 0;
                } else if (key === "WASH") {
                  // Independent/default wash-only cycles (non-spring).
                  cost = Number(spanResult.independentWashCost) || 0;
                } else if (repairLabel === key) {
                  // For paint years, only Spring wash stays separate.
                  // Default wash that is part of the paint process remains in the same repair row (TU/OC/RE).
                  const totalCost = Number(spanResult.totalCost) || 0;
                  const springOnly = Number(spanResult.springWashCost) || 0;
                  cost = Math.max(0, totalCost - springOnly);
                }

                if (cost > 0) {
                  activityNode.years[yearIdx].cost =
                    Math.round(cost * 100) / 100;
                }
              }

              node.children.push(activityNode);
            });
          }

          // Recursively build children
          if (element.children && element.children.length > 0) {
            const childNodes = buildHierarchyWithCosts(
              element.children,
              element.levelNumber,
            );

            // Separate structural children (actual bridge elements) from maintenance/activity children
            const structuralChildren = childNodes.filter(
              (child) =>
                child.type !== "maintenance" && child.type !== "activity",
            );
            const nonStructuralChildren = childNodes.filter(
              (child) =>
                child.type === "maintenance" || child.type === "activity",
            );

            // Set flag for whether this node has structural children
            // Add all children (structural + maintenance/activity)
            node.children = [...nonStructuralChildren, ...structuralChildren];

            // CRITICAL: If node has structural children, parent cost MUST equal sum of children
            // This ensures parent totals always match sum of direct children, regardless of own span cost
            if (structuralChildren.length > 0) {
              // ALWAYS replace parent costs with sum of structural children for each year
              // This overrides any own span cost - parent nodes should never show their own cost
              // Only leaf nodes (no structural children) should show their own span cost
              node.years.forEach((parentYear, idx) => {
                const childrenSum = structuralChildren.reduce((sum, child) => {
                  const childYear = child.years[idx];
                  return sum + (childYear ? childYear.cost : 0);
                }, 0);
                // Replace parent cost with sum of children (ignore own span cost completely)
                parentYear.cost = childrenSum;
              });
            } else {
              // If no structural children, this is a leaf node (area data level)
              // Keep the node's own span cost (already set above)
              // Aggregate maintenance/activity children costs (additive to own span cost)
              // This is for leaf nodes that only have maintenance/activity children
              nonStructuralChildren.forEach((child) => {
                child.years.forEach((childYear, idx) => {
                  if (node.years[idx]) {
                    node.years[idx].cost += childYear.cost;
                  }
                });
              });
            }

            // Aggregate areas from children to parent
            // Sum up all child areas (only count direct structural children, not repair process children)
            const directChildNodes = childNodes.filter(
              (child) =>
                child.type !== "maintenance" && child.type !== "activity",
            );
            if (directChildNodes.length > 0) {
              const totalChildArea = directChildNodes.reduce((sum, child) => {
                const childArea = parseFloat(child.area) || 0;
                return sum + childArea;
              }, 0);

              // Always use sum of children for parent area (children are more granular and accurate)
              // This ensures parent areas reflect the sum of their children
              if (totalChildArea > 0) {
                node.area = totalChildArea.toString();
              }
            }
          }

          return node;
        });
      };

      const hierarchyTree = buildHierarchyWithCosts(hierarchy);

      // Merge duplicated structural siblings that can appear from backend hierarchy expansion
      // (e.g., same direction code split by position as separate parent branches).
      const mergeDuplicateStructuralSiblings = (nodes = []) => {
        const prepared = nodes.map((node) => ({
          ...node,
          children: mergeDuplicateStructuralSiblings(node.children || []),
        }));

        const mergedByKey = new Map();

        prepared.forEach((node) => {
          const children = node.children || [];
          const hasStructuralChildren = children.some(
            (child) =>
              child.type !== "maintenance" && child.type !== "activity",
          );

          const normalizedCode = (node.code || "")
            .toString()
            .trim()
            .toUpperCase();
          const normalizedName = (node.name || "")
            .toString()
            .trim()
            .toUpperCase();
          const normalizedDirection = (node.direction || "")
            .toString()
            .trim()
            .toUpperCase();
          // Prefer display name/code first so grouped position nodes like
          // Internal/External do not collapse into one by shared direction.
          const mergeLabel =
            normalizedName || normalizedCode || normalizedDirection;

          // Only merge structural grouping nodes. Keep leaf/activity nodes distinct.
          const key =
            hasStructuralChildren && mergeLabel
              ? `L${node.level || node.type}:${mergeLabel}`
              : `ID:${node.id ?? ""}:${normalizedCode}:${normalizedName}:${node.type || ""}`;

          if (!mergedByKey.has(key)) {
            mergedByKey.set(key, {
              ...node,
              years: (node.years || []).map((y) => ({ ...y })),
              children: [...children],
            });
            return;
          }

          const existing = mergedByKey.get(key);

          const maxYears = Math.max(
            existing.years?.length || 0,
            node.years?.length || 0,
          );
          for (let i = 0; i < maxYears; i++) {
            if (!existing.years[i] && node.years?.[i]) {
              existing.years[i] = { ...node.years[i] };
              continue;
            }
            if (existing.years[i] && node.years?.[i]) {
              existing.years[i].cost =
                (existing.years[i].cost || 0) + (node.years[i].cost || 0);
              existing.years[i].degradationPercent = Math.max(
                existing.years[i].degradationPercent || 0,
                node.years[i].degradationPercent || 0,
              );
            }
          }

          const existingArea = parseFloat(existing.area || 0) || 0;
          const nodeArea = parseFloat(node.area || 0) || 0;
          existing.area = (existingArea + nodeArea).toString();

          existing.children = [...(existing.children || []), ...children];
        });

        return Array.from(mergedByKey.values()).map((node) => ({
          ...node,
          children: mergeDuplicateStructuralSiblings(node.children || []),
        }));
      };

      const mergedHierarchyTree =
        mergeDuplicateStructuralSiblings(hierarchyTree);

      // Bottom-up pass: ensure every parent's cost = sum of structural children (fixes any aggregation gaps)
      function normalizeParentCostsFromChildren(node) {
        if (!node || !node.years) return;
        const structuralChildren = (node.children || []).filter(
          (c) => c.type !== "maintenance" && c.type !== "activity",
        );
        structuralChildren.forEach((child) =>
          normalizeParentCostsFromChildren(child),
        );
        if (structuralChildren.length === 0) return;
        node.years.forEach((parentYear, idx) => {
          const sum = structuralChildren.reduce((s, child) => {
            const cy = child.years && child.years[idx];
            return s + (cy && typeof cy.cost === "number" ? cy.cost : 0);
          }, 0);
          parentYear.cost = sum;
        });
      }
      mergedHierarchyTree.forEach((root) =>
        normalizeParentCostsFromChildren(root),
      );

      // Always wrap in a bridge node so Grand Total = sum of all top-level costs
      const bridgeNode = {
        type: "bridge",
        name: selectedBridge?.name || "Main Bridge",
        code: selectedBridge?.bridgeCode || "BR-001",
        area: spans
          .reduce(
            (sum, span) =>
              sum + parseFloat(span.totalArea || span.total_area || 0),
            0,
          )
          .toString(),
        description: "Main bridge structure",
        children: mergedHierarchyTree,
        years: [],
      };

      allYears.forEach((year) => {
        bridgeNode.years.push({ year, cost: 0, degradationPercent: 0 });
      });

      // Bridge cost = sum of all top-level (structural) children so Grand Total is correct
      mergedHierarchyTree.forEach((child) => {
        child.years.forEach((childYear, idx) => {
          if (bridgeNode.years[idx]) {
            bridgeNode.years[idx].cost += childYear.cost;
          }
        });
      });

      // Aggregate areas from all children to bridge node
      // Calculate total area from all direct children (excluding repair process children)
      const directChildren = mergedHierarchyTree.filter(
        (child) => child.type !== "maintenance" && child.type !== "activity",
      );
      if (directChildren.length > 0) {
        const totalChildArea = directChildren.reduce((sum, child) => {
          const childArea = parseFloat(child.area) || 0;
          return sum + childArea;
        }, 0);

        // Update bridge area if children have area data
        if (totalChildArea > 0) {
          bridgeNode.area = totalChildArea.toString();
        }
      }

      return bridgeNode;
    } catch (error) {
      console.error("Error fetching hierarchy structure:", error);
      return buildSimpleHierarchyFallback(results, { bridgeId });
    }
  };

  const transformToHierarchy = useCallback(transformToHierarchyFn, [
    selectedBridgeId,
    selectedBridge,
    buildSimpleHierarchyFallback,
  ]);

  const loadDetailedYearData = useCallback(async () => {
    if (!detailLoadMeta?.simulationId || !detailLoadMeta?.totalYears) return;
    if (loadingDetailProgress) return;

    const { simulationId, totalYears } = detailLoadMeta;
    setLoadingDetailProgress({ loaded: 0, total: totalYears });
    const spansCount = Number(simulationResults?.spans?.length || 0);
    const isVeryLargeRun = spansCount >= 3000;
    const isLargeRun = spansCount >= 1500;

    // Large runs can exceed gateway/browser limits when fetching many years at once.
    // Keep batches small for very large runs, but fetch multiple requests in parallel.
    const YEAR_BATCH_FETCH_CONCURRENCY = isVeryLargeRun
      ? 2
      : isLargeRun
        ? 3
        : 4;
    const YEAR_BATCH_SIZE = isVeryLargeRun ? 1 : isLargeRun ? 2 : 6;
    const YEAR_ABORT_MS = isVeryLargeRun ? 180000 : isLargeRun ? 120000 : 60000;

    const fetchYearBatch = async (startIndex, count, retries = 1) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), YEAR_ABORT_MS);
        try {
          const r = await api.get(
            `/new-simulation/results/${simulationId}/years-batch`,
            {
              params: { startIndex, count },
              timeout: YEAR_ABORT_MS + 5000,
              signal: controller.signal,
            },
          );
          clearTimeout(timeoutId);
          if (!r.data?.success || !r.data?.data) return null;
          return Array.isArray(r.data.data.years) ? r.data.data.years : null;
        } catch (err) {
          clearTimeout(timeoutId);
          if (attempt === retries) return null;
        }
      }
      return null;
    };

    const fetchSingleYear = async (yearIndex, retries = 1) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), YEAR_ABORT_MS);
        try {
          const r = await api.get(
            `/new-simulation/results/${simulationId}/years/${yearIndex}`,
            {
              timeout: YEAR_ABORT_MS + 5000,
              signal: controller.signal,
            },
          );
          clearTimeout(timeoutId);
          if (!r.data?.success || !r.data?.data) return null;
          return r.data.data;
        } catch (err) {
          clearTimeout(timeoutId);
          if (attempt === retries) return null;
        }
      }
      return null;
    };

    const mapWithConcurrency = async (items, concurrency, mapper) => {
      const safeConcurrency = Math.max(1, Math.floor(Number(concurrency) || 1));
      const output = new Array(items.length);
      let cursor = 0;

      const workers = Array.from({
        length: Math.min(safeConcurrency, items.length),
      }).map(async () => {
        while (true) {
          const index = cursor;
          cursor += 1;
          if (index >= items.length) return;
          output[index] = await mapper(items[index], index);
        }
      });

      await Promise.all(workers);
      return output;
    };

    try {
      const batchStarts = [];
      for (let i = 0; i < totalYears; i += YEAR_BATCH_SIZE) {
        batchStarts.push(i);
      }

      let completed = 0;
      const loadedYears = await mapWithConcurrency(
        batchStarts,
        YEAR_BATCH_FETCH_CONCURRENCY,
        async (startIndex) => {
          const count = Math.min(YEAR_BATCH_SIZE, totalYears - startIndex);
          let years = await fetchYearBatch(startIndex, count);

          // Recovery path: if a multi-year batch fails, retry year-by-year.
          if (!years) {
            const singleYears = [];
            for (let i = 0; i < count; i++) {
              // eslint-disable-next-line no-await-in-loop
              const yearData = await fetchSingleYear(startIndex + i, 1);
              singleYears.push(yearData);
            }
            years = singleYears;
          }

          completed += count;
          setLoadingDetailProgress((p) =>
            p ? { ...p, loaded: Math.min(p.total, completed) } : null,
          );

          // Progressive rendering: apply each fetched batch immediately so the grid fills incrementally.
          setSimulationResults((prev) => {
            if (!prev || !Array.isArray(prev.yearlyResults)) return prev;

            const next = {
              ...prev,
              yearlyResults: [...prev.yearlyResults],
            };

            for (let i = 0; i < count; i++) {
              const targetIndex = startIndex + i;
              if (targetIndex >= next.yearlyResults.length) continue;

              const data = Array.isArray(years) ? years[i] : null;
              if (data) {
                next.yearlyResults[targetIndex] = data;
                continue;
              }

              const existing = next.yearlyResults[targetIndex];
              next.yearlyResults[targetIndex] =
                existing && typeof existing === "object"
                  ? { ...existing, spanResults: [] }
                  : { year: targetIndex + 1, totalCost: 0, spanResults: [] };
            }

            return next;
          });

          return { startIndex, count, years };
        },
      );

      if (!simulationResults || !simulationResults.yearlyResults) {
        setError(
          "Detailed simulation rows could not be prepared from summary state. Please re-run the simulation.",
        );
        return;
      }

      const mergedResults = {
        ...simulationResults,
        yearlyResults: [...simulationResults.yearlyResults],
      };

      const applyFallbackYear = (idx) => {
        const existing = simulationResults.yearlyResults[idx];
        mergedResults.yearlyResults[idx] =
          existing && typeof existing === "object"
            ? { ...existing, spanResults: [] }
            : { year: idx + 1, totalCost: 0, spanResults: [] };
      };

      for (const batch of loadedYears) {
        const startIndex = Number(batch?.startIndex ?? 0);
        const count = Number(batch?.count ?? 0);
        const years = Array.isArray(batch?.years) ? batch.years : null;

        for (let i = 0; i < count; i++) {
          const targetIndex = startIndex + i;
          if (targetIndex >= totalYears) continue;

          const data = years ? years[i] : null;
          if (data) mergedResults.yearlyResults[targetIndex] = data;
          else applyFallbackYear(targetIndex);
        }
      }

      setSimulationResults(mergedResults);

      if (mergedResults?.yearlyResults?.length) {
        const hasFullData = mergedResults.yearlyResults.every(
          (yr) => yr && yr.spanResults != null,
        );
        const hasAnySpanRows = mergedResults.yearlyResults.some(
          (yr) => Array.isArray(yr?.spanResults) && yr.spanResults.length > 0,
        );

        if (!hasAnySpanRows) {
          setError(
            "Detailed simulation rows could not be loaded from batch requests. Retried year-by-year but no span rows were returned. Please re-run the simulation.",
          );
        }

        if (hasFullData) {
          try {
            const hierarchy = await transformToHierarchy(mergedResults);
            setSimulationResults((r) => (r ? { ...r, hierarchy } : r));
          } catch (err) {
            console.error("Error transforming hierarchy:", err);
            setSimulationResults((r) => (r ? { ...r, hierarchy: null } : r));
          }
        }
      }
      setDetailLoadMeta(null);
    } catch (err) {
      console.error("Error loading year-by-year:", err);
    } finally {
      setLoadingDetailProgress(null);
    }
  }, [
    detailLoadMeta,
    loadingDetailProgress,
    transformToHierarchy,
    simulationResults,
  ]);

  useEffect(() => {
    if (detailLoadMeta && !loadingDetailProgress) {
      loadDetailedYearData();
    }
  }, [detailLoadMeta, loadingDetailProgress, loadDetailedYearData]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-sm text-gray-600">
          Run detailed degradation and cost simulations with hierarchical
          results display
        </p>
        <InlineFieldTooltip
          page="enhanced_simulation_results"
          section="page_description"
          fieldKey="simulation_engine_overview"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
          {error}
        </div>
      )}

      {/* Bridge Selection */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            1. Select Bridge
          </h2>
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bridge
            </label>
            <select
              value={selectedBridgeId}
              onChange={(e) => setSelectedBridgeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select a bridge --</option>
              {bridges.map((bridge) => (
                <option key={bridge.id} value={bridge.id}>
                  {bridge.bridgeName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Element Selection */}
      {selectedBridgeId && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  2. Select Elements
                </h2>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-base font-bold text-gray-900 sm:text-lg">
                    {selectionSummaryText}
                  </span>
                  <span className="text-base font-bold text-blue-700 sm:text-lg">
                    • {selectionAreaText}
                  </span>
                  {selectedSpanIds.length > 100 && (
                    <span className="text-sm font-semibold text-yellow-600">
                      Large simulation
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Manual</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={selectAllMode}
                    onClick={() => {
                      if (selectAllMode) {
                        handleDeselectAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                    disabled={!selectedBridge || bulkSelecting}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${selectAllMode ? "bg-blue-600" : "bg-gray-300"}`}
                    title="Toggle full bridge selection"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${selectAllMode ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                  <span className="text-sm text-gray-700">Select All</span>
                </div>

                {!selectAllMode && (
                  <button
                    type="button"
                    onClick={handleOpenAddBridgeSelection}
                    disabled={!selectedBridge}
                    className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Bridge Selection
                  </button>
                )}
              </div>
            </div>

            {selectAllMode ? (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-4 text-sm font-medium text-blue-800">
                {bulkSelecting ? "Loading full bridge selection..." : "Full bridge selected"}
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-gray-200">
              {bridgeSelections.length === 0 ? (
                <div className="bg-gray-50 px-4 py-6 text-sm text-gray-600">
                  Add one or more bridge selections to choose the elements used by the simulation.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Selection Path
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">
                          Area (m²)
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">
                          Active
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {bridgeSelections.map((selection) => (
                        <tr
                          key={selection.id}
                          className={selection.active ? "" : "bg-gray-50 text-gray-500"}
                        >
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-900">
                              {selection.pathSummary ||
                                `${selection.elementIds?.length || 0} ${totalAreaDataEntityLabel.toLowerCase()}`}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {selection.elementIds?.length || 0} {totalAreaDataEntityLabel.toLowerCase()}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right align-top font-medium text-gray-900">
                            {(Number(selection.area) || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center align-top">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={selection.active}
                              onClick={() => handleToggleBridgeSelection(selection.id)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${selection.active ? "bg-blue-600" : "bg-gray-300"}`}
                              title={selection.active ? "Disable selection" : "Enable selection"}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${selection.active ? "translate-x-6" : "translate-x-1"}`}
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => handleOpenEditBridgeSelection(selection.id)}
                                className="rounded p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                title="Edit selection"
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteBridgeSelection(selection.id)}
                                className="rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-800"
                                title="Delete selection"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            )}

            <div className="mt-4 overflow-hidden rounded-md border border-gray-300 bg-gray-50">
              <button
                type="button"
                onClick={() => setIsSelectionSummaryCollapsed((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selection Summary
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Expand to review the selected {totalAreaDataEntityLabel.toLowerCase()}.
                  </p>
                </div>
                <span className="flex items-center gap-3">
                  <span className="hidden text-sm font-semibold text-gray-700 sm:inline">
                    {selectionSummaryText}
                  </span>
                  <FontAwesomeIcon
                    icon={
                      isSelectionSummaryCollapsed
                        ? faChevronRight
                        : faChevronDown
                    }
                    className="text-gray-500"
                  />
                </span>
              </button>

              {!isSelectionSummaryCollapsed &&
                (loadingSelectionTable ? (
                  <div className="border-t border-gray-300 px-4 py-3 text-sm text-gray-500">
                    Building table...
                  </div>
                ) : (
                  <div className="border-t border-gray-300 p-4">
                    <div
                      className="overflow-x-auto overflow-y-auto"
                      style={{ maxHeight: "390px" }}
                    >
                      <table className="min-w-full text-sm border border-gray-200 rounded bg-white">
                        <thead className="bg-gray-100">
                          <tr>
                            {selectionSummaryLevels.map((levelNum) => (
                              <th
                                key={levelNum}
                                className="px-3 py-2 text-left font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10"
                              >
                                {getRawLevelLabel(levelNum)}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-right font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10">
                              Area (m²)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectionTableRows.map((row) => (
                            <tr key={row.id} className="border-t border-gray-200">
                              {selectionSummaryLevels.map((levelNum) => (
                                <td
                                  key={`${row.id}-${levelNum}`}
                                  className="px-3 py-2 text-gray-900"
                                >
                                  {row.levels[`level${levelNum}`] ||
                                    (levelNum === 1
                                      ? row.direction || "-"
                                      : levelNum === 2
                                        ? row.position || "-"
                                        : "-")}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-right text-gray-900">
                                {(row.area || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>

            {!isSpanSelectionCollapsed && (
              <>
                {/* Hierarchical Cascading Selection with Listboxes */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Select elements through the hierarchy. The last two lists
                    support multi-select checkboxes and simulation runs on the
                    level that holds area data.
                  </p>

                  {selectAllMode ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
                      All elements are currently selected at the area level.
                      Turn off the Select All switch to view and choose lists
                      manually.
                    </div>
                  ) : (
                    <>
                      {/* Top Level Filters */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* Item (formerly Direction) Filter */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Item
                          </label>
                          <select
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={selectedPath.direction || ""}
                            onChange={(e) =>
                              handleDirectionListboxSelect(
                                e.target.value || null,
                              )
                            }
                          >
                            <option value="">All Items</option>
                            {hierarchyData.directions?.map((dir) => (
                              <option key={dir} value={dir}>
                                {dir}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Position Filter (Internal / External) */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Position
                          </label>
                          <select
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={selectedPath.position || ""}
                            onChange={(e) =>
                              handlePositionListboxSelect(
                                e.target.value || null,
                              )
                            }
                            disabled={!selectedPath.direction}
                          >
                            <option value="">All Positions</option>
                            {hierarchyData.positions?.map((pos) => {
                              const displayPos =
                                pos === "Internal" || pos === "External"
                                  ? pos
                                  : String(pos)
                                      .replace(/^P\d+\s*/i, "")
                                      .trim() || pos;
                              return (
                                <option key={pos} value={pos}>
                                  {displayPos}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      {/* Hierarchical Columns - Listboxes: Item (Direction) → Position → Level 1 → ... → Area */}
                      <div className="flex gap-2 overflow-x-auto pb-4">
                        {/* Column 1: Item (unique directions - one NB, one SB, one TC) */}
                        <div
                          className="flex-shrink-0"
                          style={{ minWidth: "140px", maxWidth: "140px" }}
                        >
                          <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                            <span>Item</span>
                            <input
                              type="checkbox"
                              title="Select all items and all descendants"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleSelectAll();
                                } else {
                                  handleDeselectAll();
                                }
                              }}
                              disabled={!selectedBridgeId || bulkSelecting}
                            />
                          </label>
                          <div
                            className="border border-gray-300 rounded-md bg-white"
                            style={{
                              height: "320px",
                              minHeight: "320px",
                              overflowY: "auto",
                            }}
                          >
                            {loadingHierarchy &&
                            !hierarchyData.directions?.length ? (
                              <div className="p-2 text-center text-gray-500 text-xs">
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  spin
                                  className="mr-1"
                                />
                                Loading...
                              </div>
                            ) : !hierarchyData.directions?.length ? (
                              <div className="p-2 text-center text-gray-400 text-xs">
                                No items
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {(hierarchyData.directions || []).map((dir) => {
                                  const isSelected =
                                    selectedPath.direction === dir;
                                  return (
                                    <div
                                      key={dir}
                                      className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                        isSelected
                                          ? "bg-blue-100 border-l-2 border-blue-500"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        handleDirectionListboxSelect(
                                          isSelected ? null : dir,
                                        )
                                      }
                                    >
                                      <div
                                        className="font-medium text-xs text-gray-900 truncate"
                                        title={dir}
                                      >
                                        {dir}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {hierarchyData.directions?.length > 0 && (
                            <div className="mt-0.5 text-xs text-gray-500 text-center">
                              {hierarchyData.directions.length}
                            </div>
                          )}
                        </div>
                        {/* Column 2: Position (Internal / External; filtered by Item when Item selected) */}
                        <div
                          className="flex-shrink-0"
                          style={{ minWidth: "140px", maxWidth: "140px" }}
                        >
                          <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                            <span>Position</span>
                            <input
                              type="checkbox"
                              title="Select all positions and all descendants"
                              onChange={(e) =>
                                handlePositionCascadeSelectAll(e.target.checked)
                              }
                              disabled={
                                !selectedPath.direction || bulkSelecting
                              }
                            />
                          </label>
                          <div
                            className="border border-gray-300 rounded-md bg-white"
                            style={{
                              height: "320px",
                              minHeight: "320px",
                              overflowY: "auto",
                            }}
                          >
                            {!selectedPath.direction ? (
                              <div className="p-2 text-center text-gray-400 text-xs">
                                Select Item first
                              </div>
                            ) : loadingHierarchy &&
                              !hierarchyData.positions?.length ? (
                              <div className="p-2 text-center text-gray-500 text-xs">
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  spin
                                  className="mr-1"
                                />
                                Loading...
                              </div>
                            ) : !hierarchyData.positions?.length ? (
                              <div className="p-2 text-center text-gray-400 text-xs">
                                No positions
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {(hierarchyData.positions || []).map((pos) => {
                                  const isSelected =
                                    selectedPath.position === pos;
                                  const displayPos =
                                    pos === "Internal" || pos === "External"
                                      ? pos
                                      : String(pos)
                                          .replace(/^P\d+\s*/i, "")
                                          .trim() || pos;
                                  return (
                                    <div
                                      key={pos}
                                      className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                        isSelected
                                          ? "bg-blue-100 border-l-2 border-blue-500"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        handlePositionListboxSelect(
                                          isSelected ? null : pos,
                                        )
                                      }
                                    >
                                      <div
                                        className="font-medium text-xs text-gray-900 truncate"
                                        title={displayPos}
                                      >
                                        {displayPos}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {hierarchyData.positions?.length > 0 && (
                            <div className="mt-0.5 text-xs text-gray-500 text-center">
                              {hierarchyData.positions.length}
                            </div>
                          )}
                        </div>
                        {/* Columns 3+: Frame, Span, ... (skip Level 1 and 2 to avoid repeating Item/Position) */}
                        {levelsToShowAfterFilters.map((levelNum) => {
                          const levelKey = `level${levelNum}`;
                          const levelLabel = getLevelLabel(levelNum);
                          const isAreaDataLevel =
                            levelNum === getAreaDataLevel();
                          const isPenultimateLevel =
                            levelNum === penultimateLevel;
                          const elements = hierarchyData[levelKey] || [];
                          const selectedId = selectedPath[levelKey] || null;
                          const isLevelEnabled =
                            levelNum === firstVisibleLevel
                              ? Boolean(
                                  selectedPath.direction &&
                                  selectedPath.position,
                                )
                              : Boolean(selectedPath[`level${levelNum - 1}`]);
                          const areaLevelKey = `level${getAreaDataLevel()}`;

                          return (
                            <div
                              key={levelNum}
                              className="flex-shrink-0"
                              style={{ minWidth: "140px", maxWidth: "140px" }}
                            >
                              <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                                <span>
                                  {levelLabel}
                                  {isAreaDataLevel && (
                                    <span className="ml-1 text-xs text-blue-600 font-normal">
                                      (Area)
                                    </span>
                                  )}
                                </span>
                                <input
                                  type="checkbox"
                                  title={`Select all ${levelLabel} and all descendants`}
                                  onChange={(e) =>
                                    handleLevelCascadeSelectAll(
                                      levelNum,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={
                                    !elements.length ||
                                    !isLevelEnabled ||
                                    bulkSelecting
                                  }
                                />
                              </label>
                              <div
                                className="border border-gray-300 rounded-md bg-white"
                                style={{
                                  height: "320px",
                                  minHeight: "320px",
                                  overflowY: "auto",
                                }}
                              >
                                {loadingHierarchy && elements.length === 0 ? (
                                  <div className="p-2 text-center text-gray-500 text-xs">
                                    <FontAwesomeIcon
                                      icon={faSpinner}
                                      spin
                                      className="mr-1"
                                    />
                                    Loading...
                                  </div>
                                ) : elements.length === 0 ? (
                                  <div className="p-2 text-center text-gray-400 text-xs">
                                    {isLevelEnabled
                                      ? "No items"
                                      : levelNum === firstVisibleLevel
                                        ? "Select Item and Position"
                                        : "Select parent"}
                                  </div>
                                ) : (
                                  <div className="divide-y divide-gray-200">
                                    {elements.map((element) => {
                                      // For area data level, use multi-select with checkboxes
                                      if (isAreaDataLevel) {
                                        const isSelected =
                                          selectedSpanIds.includes(element.id);
                                        const displayName =
                                          element.element_name ||
                                          element.element_code;
                                        return (
                                          <div
                                            key={element.id}
                                            className={`py-0.5 px-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                              isSelected
                                                ? "bg-blue-100 border-l-2 border-blue-500"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              if (isLevelEnabled) {
                                                handleAreaDataSelection(
                                                  element.id,
                                                  !isSelected,
                                                );
                                              }
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handleAreaDataSelection(
                                                  element.id,
                                                  e.target.checked,
                                                );
                                              }}
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              className="cursor-pointer flex-shrink-0"
                                              disabled={!isLevelEnabled}
                                            />
                                            <div
                                              className="flex-1 min-w-0 font-medium text-xs text-gray-900 truncate"
                                              title={displayName}
                                            >
                                              {displayName}
                                            </div>
                                          </div>
                                        );
                                      } else if (isPenultimateLevel) {
                                        const cachedDescendantIds =
                                          descendantAreaIdsByParentId[
                                            element.id
                                          ] ||
                                          (selectedId === element.id
                                            ? (
                                                hierarchyData[areaLevelKey] ||
                                                []
                                              )
                                                .map((item) => item.id)
                                                .filter(Boolean)
                                            : []);
                                        const isChecked =
                                          cachedDescendantIds.length > 0 &&
                                          cachedDescendantIds.every((id) =>
                                            selectedSpanIds.includes(id),
                                          );
                                        const displayName =
                                          element.element_name ||
                                          element.element_code ||
                                          `ID ${element.id}`;

                                        return (
                                          <div
                                            key={element.id}
                                            className={`py-0.5 px-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                              selectedId === element.id
                                                ? "bg-blue-100 border-l-2 border-blue-500"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              if (isLevelEnabled) {
                                                handleLevelSelection(
                                                  levelNum,
                                                  selectedId === element.id
                                                    ? null
                                                    : element.id,
                                                );
                                              }
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                handlePenultimateSelection(
                                                  levelNum,
                                                  element,
                                                  e.target.checked,
                                                );
                                              }}
                                              className="cursor-pointer flex-shrink-0"
                                              disabled={
                                                !isLevelEnabled || bulkSelecting
                                              }
                                            />
                                            <div
                                              className="flex-1 min-w-0 font-medium text-xs text-gray-900 truncate"
                                              title={displayName}
                                            >
                                              {displayName}
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        // For navigation levels, use single selection; show element name only (user-friendly)
                                        const isSelected =
                                          selectedId === element.id;
                                        const displayName =
                                          element.element_name ||
                                          element.element_code ||
                                          `ID ${element.id}`;
                                        return (
                                          <div
                                            key={element.id}
                                            className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                              isSelected
                                                ? "bg-blue-100 border-l-2 border-blue-500"
                                                : ""
                                            }`}
                                            onClick={() => {
                                              if (isLevelEnabled) {
                                                handleLevelSelection(
                                                  levelNum,
                                                  isSelected
                                                    ? null
                                                    : element.id,
                                                );
                                              }
                                            }}
                                          >
                                            <div
                                              className="font-medium text-xs text-gray-900 truncate"
                                              title={displayName}
                                            >
                                              {displayName}
                                            </div>
                                          </div>
                                        );
                                      }
                                    })}
                                  </div>
                                )}
                              </div>
                              {elements.length > 0 && (
                                <div className="mt-0.5 text-xs text-gray-500 text-center">
                                  {elements.length}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Selection Summary */}
                  <div className="mt-4 overflow-hidden rounded-md border border-gray-300 bg-gray-50">
                    <button
                      type="button"
                      onClick={() =>
                        setIsSelectionSummaryCollapsed((prev) => !prev)
                      }
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-700">
                          Selection Summary
                        </h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Expand to review the selected{" "}
                          {totalAreaDataEntityLabel.toLowerCase()}.
                        </p>
                      </div>
                      <span className="flex items-center gap-3">
                        <span className="hidden text-sm font-semibold text-gray-700 sm:inline">
                          {selectionSummaryText}
                        </span>
                        <FontAwesomeIcon
                          icon={
                            isSelectionSummaryCollapsed
                              ? faChevronRight
                              : faChevronDown
                          }
                          className="text-gray-500"
                        />
                      </span>
                    </button>
                    {!isSelectionSummaryCollapsed &&
                      (loadingSelectionTable ? (
                        <div className="border-t border-gray-300 px-4 py-3 text-sm text-gray-500">
                          Building table...
                        </div>
                      ) : (
                        <div className="border-t border-gray-300 p-4">
                          <div
                            className="overflow-x-auto overflow-y-auto"
                            style={{ maxHeight: "390px" }}
                          >
                            <table className="min-w-full text-sm border border-gray-200 rounded bg-white">
                              <thead className="bg-gray-100">
                                <tr>
                                  {selectionSummaryLevels.map((levelNum) => (
                                    <th
                                      key={levelNum}
                                      className="px-3 py-2 text-left font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10"
                                    >
                                      {getRawLevelLabel(levelNum)}
                                    </th>
                                  ))}
                                  <th className="px-3 py-2 text-right font-semibold text-gray-700 sticky top-0 bg-gray-100 z-10">
                                    Area (m²)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectionTableRows.map((row) => (
                                  <tr
                                    key={row.id}
                                    className="border-t border-gray-200"
                                  >
                                    {selectionSummaryLevels.map((levelNum) => (
                                      <td
                                        key={`${row.id}-${levelNum}`}
                                        className="px-3 py-2 text-gray-900"
                                      >
                                        {row.levels[`level${levelNum}`] ||
                                          (levelNum === 1
                                            ? row.direction || "-"
                                            : levelNum === 2
                                              ? row.position || "-"
                                              : "-")}
                                      </td>
                                    ))}
                                    <td className="px-3 py-2 text-right text-gray-900">
                                      {(row.area || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                  </div>
                  {selectedSpanIds.length > 100 && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                      ⚠️ Large simulation: {selectedSpanIds.length} components
                      selected
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <BridgeSelectionModal
        isOpen={showBridgeSelectionModal}
        bridgeId={selectedBridgeId}
        selectedBridge={selectedBridge}
        initialSelectedIds={editingBridgeSelection?.elementIds || []}
        onClose={handleCloseBridgeSelectionModal}
        onSave={handleSaveBridgeSelection}
        title={editingBridgeSelection ? "Edit Bridge Selection" : "Add Bridge Selection"}
        saveLabel={editingBridgeSelection ? "Save Changes" : "Save Selection"}
      />

      {/* Scenario Configuration */}
      {selectedSpanIds.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <button
                  className="mr-3 text-gray-500 hover:text-gray-700"
                  onClick={() =>
                    setIsCalculationDetailsCollapsed(
                      !isCalculationDetailsCollapsed,
                    )
                  }
                  title={
                    isCalculationDetailsCollapsed
                      ? "Expand scenario configuration"
                      : "Collapse scenario configuration"
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      isCalculationDetailsCollapsed
                        ? faChevronRight
                        : faChevronDown
                    }
                  />
                </button>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <span>3. Configure Scenario</span>
                  <InlineFieldTooltip
                    page="enhanced_simulation_results"
                    section="scenario_configuration"
                    fieldKey="configure_scenario_header"
                  />
                </h2>
              </div>
            </div>

            {!isCalculationDetailsCollapsed && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-6">
                {/* Time Horizon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Horizon (Years)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={scenario.timeHorizonYears}
                    onChange={(e) =>
                      handleScenarioChange(
                        "timeHorizonYears",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Inflation Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Inflation Rate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    step="0.1"
                    value={scenario.discountRate * 100}
                    onChange={(e) =>
                      handleScenarioChange(
                        "discountRate",
                        parseFloat(e.target.value) / 100,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Estimated Painted Area Percent */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Painted Area (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={scenario.estimatedPaintedAreaPercent}
                    onChange={(e) => {
                      const parsedValue = parseFloat(e.target.value);
                      handleScenarioChange(
                        "estimatedPaintedAreaPercent",
                        Number.isNaN(parsedValue) ? 0 : parsedValue,
                      );
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobilization Weeks Limit
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="104"
                    step="1"
                    value={scenario.mobilizationWeeksLimit ?? 35}
                    onChange={(e) => {
                      const parsedValue = parseInt(e.target.value, 10);
                      handleScenarioChange(
                        "mobilizationWeeksLimit",
                        Number.isNaN(parsedValue)
                          ? 35
                          : Math.max(1, parsedValue),
                      );
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    # Foremen
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={scenario.foremansCountLimit ?? 1}
                    onChange={(e) => {
                      const parsedValue = parseInt(e.target.value, 10);
                      handleScenarioChange(
                        "foremansCountLimit",
                        Number.isNaN(parsedValue)
                          ? 1
                          : Math.max(0, parsedValue),
                      );
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    #Layers per Day
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={scenario.layersPerDay ?? 1}
                    onChange={(e) => {
                      const parsedValue = parseFloat(e.target.value);
                      handleScenarioChange(
                        "layersPerDay",
                        Number.isNaN(parsedValue)
                          ? scenario.layersPerDay
                          : Math.max(0.01, parsedValue),
                      );
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Damage Split by category */}
            {!isCalculationDetailsCollapsed &&
              (() => {
                const categories = [
                  ...new Set([
                    ...(damageSplitDefaultsFromSettings || [])
                      .map((d) => d.corrosionCategory || d.corrosion_category)
                      .filter(Boolean),
                    ...Object.keys(scenario.damageSplitByCategory || {}),
                  ]),
                ].sort();
                const fallback = scenario.damageSplit || {
                  A: 70,
                  B: 20,
                  C: 10,
                };
                const useDamageSplitPercent = isDamageSplitMode(scenario);
                return (
                  <div className="mt-6">
                    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="flex items-center gap-2 text-md font-medium text-gray-900">
                        <span>Damage Allocation</span>
                        <InlineFieldTooltip
                          page="enhanced_simulation_results"
                          section="scenario_configuration"
                          fieldKey="damage_split_by_category"
                        />
                      </h3>
                      <div className="inline-flex rounded-md border border-gray-300 bg-white p-1 shadow-sm">
                        <button
                          type="button"
                          aria-pressed={!useDamageSplitPercent}
                          onClick={() =>
                            handleDamageAllocationModeChange(
                              DAMAGE_ALLOCATION_MODES.ELEMENT_DAMAGE_LEVEL,
                            )
                          }
                          className={`px-3 py-1.5 text-sm font-medium rounded ${
                            !useDamageSplitPercent
                              ? "bg-blue-600 text-white shadow-sm"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Element Damage Level
                        </button>
                        <button
                          type="button"
                          aria-pressed={useDamageSplitPercent}
                          onClick={() =>
                            handleDamageAllocationModeChange(
                              DAMAGE_ALLOCATION_MODES.DAMAGE_SPLIT,
                            )
                          }
                          className={`px-3 py-1.5 text-sm font-medium rounded ${
                            useDamageSplitPercent
                              ? "bg-blue-600 text-white shadow-sm"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          Damage Split %
                        </button>
                      </div>
                    </div>
                    {!useDamageSplitPercent && (
                      <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                        Each element uses its own stored corrosion/damage
                        combination(s). An element stored as{" "}
                        <span className="font-mono text-xs">C5B</span> puts 100%
                        of its repair area on B. An element split across
                        combinations — for example{" "}
                        <span className="font-mono text-xs">C2A-60;C3B-40</span>{" "}
                        — divides its area accordingly, and each part degrades
                        and falls due for repair on its own schedule.
                      </div>
                    )}
                    {useDamageSplitPercent && categories.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-200 rounded-md">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="px-3 py-2 text-left text-sm font-medium text-gray-700 border-b">
                                Category
                              </th>
                              <th className="px-3 py-2 text-right text-sm font-medium text-gray-700 border-b">
                                Severity A %
                              </th>
                              <th className="px-3 py-2 text-right text-sm font-medium text-gray-700 border-b">
                                Severity B %
                              </th>
                              <th className="px-3 py-2 text-right text-sm font-medium text-gray-700 border-b">
                                Severity C %
                              </th>
                              <th className="px-3 py-2 text-right text-sm font-medium text-gray-700 border-b">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {categories.map((cat) => {
                              const row =
                                (scenario.damageSplitByCategory || {})[cat] ||
                                fallback;
                              const a = Number(row.A) || 0;
                              const b = Number(row.B) || 0;
                              const c = Number(row.C) || 0;
                              const total = a + b + c;
                              return (
                                <tr
                                  key={cat}
                                  className="border-b border-gray-100"
                                >
                                  <td className="px-3 py-2 text-sm font-medium text-gray-800">
                                    {cat}
                                  </td>
                                  <td className="px-2 py-1">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={a}
                                      onChange={(e) =>
                                        handleDamageSplitByCategoryChange(
                                          cat,
                                          "A",
                                          e.target.value,
                                        )
                                      }
                                      className="w-14 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={b}
                                      onChange={(e) =>
                                        handleDamageSplitByCategoryChange(
                                          cat,
                                          "B",
                                          e.target.value,
                                        )
                                      }
                                      className="w-14 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={c}
                                      onChange={(e) =>
                                        handleDamageSplitByCategoryChange(
                                          cat,
                                          "C",
                                          e.target.value,
                                        )
                                      }
                                      className="w-14 px-2 py-1 border border-gray-300 rounded text-right text-sm"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right text-sm">
                                    {total}%
                                    {total !== 100 && (
                                      <span className="text-red-600 ml-1">
                                        (≠100)
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : useDamageSplitPercent ? (
                      <p className="text-sm text-gray-500">
                        Load Damage Split Defaults (Corrosion &amp; Damage) to
                        edit per-category splits. Fallback: A=70%, B=20%, C=10%.
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-gray-500">
                      {useDamageSplitPercent
                        ? "Each element keeps its own exposure class(es); within each class the area is spread across A/B/C using these per-category values when present, otherwise Damage Split Defaults or the single fallback split."
                        : "Damage split percentages are hidden and ignored while Element Damage Level is selected."}
                    </p>
                  </div>
                );
              })()}

            {/* Monte Carlo Configuration */}
            {!isCalculationDetailsCollapsed && (
              <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg">
                <label className="flex items-center cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={useMonteCarloMode}
                    onChange={(e) => setUseMonteCarloMode(e.target.checked)}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-3 text-lg font-semibold text-purple-900">
                    🎲 Enable Monte Carlo Simulation (Risk Analysis)
                  </span>
                </label>
                <p className="text-sm text-purple-700 mb-4">
                  Run probabilistic simulations to analyze cost variability and
                  budget risk with confidence intervals.
                  {useMonteCarloMode
                    ? " Monte Carlo mode enabled - will run multiple iterations with randomized parameters."
                    : " Enable to see P10, P50, P90 budget scenarios and risk assessment."}
                </p>

                {useMonteCarloMode && (
                  <div className="p-4 bg-white rounded-lg border border-purple-100">
                    <h4 className="font-semibold text-gray-900 mb-4">
                      Monte Carlo Parameters
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Iterations
                        </label>
                        <select
                          value={monteCarloConfig.iterations}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              iterations: parseInt(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="100">100 (Quick Test)</option>
                          <option value="500">500 (Fast)</option>
                          <option value="1000">1,000 (Standard)</option>
                          <option value="2000">2,000 (Detailed)</option>
                          <option value="5000">5,000 (High Precision)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          More iterations = more accurate but slower
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cost Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={monteCarloConfig.costVariation}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              costVariation: parseFloat(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Materials & labor uncertainty
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Degradation Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={monteCarloConfig.degradationVariation}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              degradationVariation: parseFloat(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Coating life uncertainty
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Inflation Rate Variation (±%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          value={monteCarloConfig.discountRateVariation}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              discountRateVariation: parseFloat(e.target.value),
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Economic uncertainty
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Repair Threshold (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={monteCarloConfig.repairThreshold * 100}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              repairThreshold: parseFloat(e.target.value) / 100,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Trigger full repair at degradation %
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maintenance Threshold (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={monteCarloConfig.maintenanceThreshold * 100}
                          onChange={(e) =>
                            setMonteCarloConfig((prev) => ({
                              ...prev,
                              maintenanceThreshold:
                                parseFloat(e.target.value) / 100,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Trigger maintenance at degradation %
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>📊 Monte Carlo Analysis:</strong> Will run{" "}
                      {monteCarloConfig.iterations.toLocaleString()} simulations
                      with randomized parameters to generate cost distribution
                      curves and confidence intervals (P10, P50, P90).
                      {selectedSpanIds.length > 5 &&
                        monteCarloConfig.iterations >= 2000 && (
                          <span className="block mt-2 text-yellow-700">
                            ⚠️ Warning: High iteration count with{" "}
                            {selectedSpanIds.length} {totalAreaDataEntityLabel.toLowerCase()} may take 2-5 minutes.
                          </span>
                        )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Optimization Toggle */}
            <div className="mt-6 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showOptimization}
                  onChange={(e) => setShowOptimization(e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Optimization Mode
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                Find the cheapest 30-year maintenance path by varying decay
                trigger percentages
              </p>
            </div>

            {/* Optimization Parameters */}
            {showOptimization && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Optimization Parameters
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min Threshold (%)
                    </label>
                    <input
                      type="number"
                      value={optimizationParams.minDecayThreshold}
                      onChange={(e) =>
                        setOptimizationParams((prev) => ({
                          ...prev,
                          minDecayThreshold: parseFloat(e.target.value) || 0.5,
                        }))
                      }
                      min="0.5"
                      max="10"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Max Threshold (%)
                    </label>
                    <input
                      type="number"
                      value={optimizationParams.maxDecayThreshold}
                      onChange={(e) =>
                        setOptimizationParams((prev) => ({
                          ...prev,
                          maxDecayThreshold: parseFloat(e.target.value) || 10.0,
                        }))
                      }
                      min="0.5"
                      max="10"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Step Size (%)
                    </label>
                    <input
                      type="number"
                      value={optimizationParams.stepSize}
                      onChange={(e) =>
                        setOptimizationParams((prev) => ({
                          ...prev,
                          stepSize: parseFloat(e.target.value) || 0.5,
                        }))
                      }
                      min="0.1"
                      max="2"
                      step="0.1"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Will test{" "}
                  {Math.ceil(
                    (optimizationParams.maxDecayThreshold -
                      optimizationParams.minDecayThreshold) /
                      optimizationParams.stepSize,
                  ) + 1}{" "}
                  threshold values
                </p>
              </div>
            )}

            {/* Run Simulation/Audit Buttons */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={showOptimization ? runOptimization : () => runSimulation()}
                disabled={
                  loading ||
                  optimizing ||
                  bulkSelecting ||
                  selectedSpanIds.length === 0
                }
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && activeRunType !== "audit" ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    {useMonteCarloMode
                      ? `Running Monte Carlo (${monteCarloConfig.iterations} iterations)...`
                      : "Running Simulation..."}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    {useMonteCarloMode
                      ? `🎲 Run Monte Carlo Simulation (${monteCarloConfig.iterations} iterations)`
                      : showOptimization
                        ? "Run Optimization"
                        : "Run Simulation"}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => runSimulation({ auditMode: true })}
                disabled={
                  loading ||
                  optimizing ||
                  bulkSelecting ||
                  showOptimization ||
                  useMonteCarloMode ||
                  selectedSpanIds.length === 0
                }
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Run a deterministic audit for the current manual selection"
              >
                {loading && activeRunType === "audit" ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Starting Audit...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faWrench} className="mr-2" />
                    Start Audit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Optimization Results */}
      {optimizationResults && (
        <div id="optimization-results" className="space-y-6 mb-6">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg shadow-sm border border-green-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Optimization Results
              </h2>
              <span className="px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-full">
                Optimal Found
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Optimal Threshold</p>
                <p className="text-2xl font-bold text-blue-600">
                  {optimizationResults.optimalThreshold}%
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Optimal NPV</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(optimizationResults.optimalNPV)}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(optimizationResults.optimalTotalCost)}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-xs text-gray-600 mb-1">Repair Count</p>
                <p className="text-2xl font-bold text-purple-600">
                  {optimizationResults.optimalRepairCount}
                </p>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow mb-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Cost Savings Analysis
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Average NPV</p>
                  <p className="font-semibold">
                    {formatCurrency(optimizationResults.statistics.averageNPV)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Min NPV</p>
                  <p className="font-semibold text-green-600">
                    {formatCurrency(optimizationResults.statistics.minNPV)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Max NPV</p>
                  <p className="font-semibold text-red-600">
                    {formatCurrency(optimizationResults.statistics.maxNPV)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Potential Savings</p>
                  <p className="font-semibold text-blue-600">
                    {formatCurrency(optimizationResults.statistics.costSavings)}{" "}
                    ({optimizationResults.statistics.costSavingsPercent}%)
                  </p>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <h3 className="text-sm font-semibold text-gray-900 p-4 border-b">
                All Tested Thresholds
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Threshold (%)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        NPV
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Total Cost
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Repairs
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {optimizationResults.allResults.map((result, idx) => (
                      <tr
                        key={idx}
                        className={result.isOptimal ? "bg-green-50" : ""}
                      >
                        <td className="px-4 py-2 text-sm font-medium">
                          {result.decayThreshold}%
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {formatCurrency(result.npv)}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {formatCurrency(result.totalCost)}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {result.repairCount || 0}
                        </td>
                        <td className="px-4 py-2">
                          {result.isOptimal ? (
                            <span className="px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded">
                              Optimal
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Results */}
      {simulationResults && (
        <div id="simulation-results" className="space-y-6">
          {loadingDetailProgress && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
              <span className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
              <span className="text-blue-800">
                Loading detailed results… {loadingDetailProgress.loaded}/
                {loadingDetailProgress.total} years
              </span>
            </div>
          )}

          {loadingMobilizationYears && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
              Loading lightweight mobilization inputs...
            </div>
          )}
          {/* Export Buttons */}
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {optimizationResults
                  ? "Optimal Strategy Results"
                  : "Simulation Results"}
              </h2>
              <div className="flex items-center space-x-3">
                {simulationResults.mode === "monte_carlo" &&
                  renderSaveSnapshotButton()}
                <button
                  onClick={() =>
                    exportSimulationToExcel(
                      simulationResults,
                      simulationResults.hierarchy,
                      selectedBridge,
                      showDecayPercent,
                    )
                  }
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  title="Export to Excel"
                >
                  <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                  Export Excel
                </button>
                <button
                  onClick={() =>
                    exportSimulationToPDFUtil(
                      simulationResults,
                      simulationResults.hierarchy,
                      selectedBridge,
                      showDecayPercent,
                    )
                  }
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  title="Export to PDF"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  Export PDF
                </button>
              </div>
            </div>
          </div>

          {/* Monte Carlo Results (if enabled) */}
          {simulationResults.mode === "monte_carlo" && (
            <MonteCarloResults results={simulationResults} />
          )}

          {/* Summary Cards (for deterministic or as overview for Monte Carlo) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faLayerGroup}
                  className="text-2xl text-blue-600 mr-3"
                />
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total {totalAreaDataEntityLabel}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {simulationResults.spans?.length || 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faChartLine}
                  className="text-2xl text-green-600 mr-3"
                />
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Time Horizon
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {scenario.timeHorizonYears} Years
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="text-2xl text-purple-600 mr-3"
                />
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Cost
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(totalCostWithMobilization)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Simulation Summary (Deterministic mode only) */}
          {simulationResults.mode !== "monte_carlo" && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Simulation Summary
                </h2>
                <div className="text-sm text-gray-600 mb-4">
                  Totals include project-level Mobilization Costs (based on
                  yearly operation area).
                  {simulationResults.simulationStartYear != null && (
                    <span className="block mt-1">
                      Simulation start year:{" "}
                      {simulationResults.simulationStartYear} (Year 1 ={" "}
                      {simulationResults.simulationStartYear})
                    </span>
                  )}
                </div>

                {simulationResults.summary && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-blue-800">
                          Total Cost ({scenario.timeHorizonYears} Years)
                        </h3>
                        <p className="text-2xl font-bold text-blue-900">
                          {formatCurrency(totalCostWithMobilization)}
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-green-800">
                          Inflation-Adjusted Total
                        </h3>
                        <p className="text-2xl font-bold text-green-900">
                          {formatCurrency(simulationResults.summary.npv)}
                        </p>
                        {simulationResults.summary.calculationLog && (
                          <p className="text-xs text-green-700 mt-1">
                            Inflation Rate:{" "}
                            {(
                              simulationResults.summary.calculationLog
                                .inflationRate * 100
                            ).toFixed(2)}
                            %
                          </p>
                        )}
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-purple-800">
                          Average Yearly Cost
                        </h3>
                        <p className="text-2xl font-bold text-purple-900">
                          {formatCurrency(
                            simulationResults.summary.avgYearlyCost,
                          )}
                        </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-orange-800">
                          Peak Cost Year
                        </h3>
                        <p className="text-2xl font-bold text-orange-900">
                          Year{" "}
                          {simulationResults.summary.peakCostYear?.year ||
                            "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Summary Calculation Log */}
                    {isAuditResult && simulationResults.summary.calculationLog && (
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          Summary Calculation Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">
                              Inflation Rate:{" "}
                            </span>
                            <span className="text-gray-600">
                              {(
                                simulationResults.summary.calculationLog
                                  .inflationRate * 100
                              ).toFixed(2)}
                              %
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">
                              Explanation:{" "}
                            </span>
                            <span className="text-gray-600">
                              {
                                simulationResults.summary.calculationLog
                                  .explanation
                              }
                            </span>
                          </div>
                          {simulationResults.summary.calculationLog
                            .yearlyBreakdown &&
                            simulationResults.summary.calculationLog
                              .yearlyBreakdown.length > 0 && (
                              <div className="mt-4">
                                <span className="font-medium text-gray-700 block mb-2">
                                  Yearly Inflation-Adjusted Breakdown:
                                </span>
                                <div className="max-h-48 overflow-y-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-2 py-1 text-left">
                                          Year
                                        </th>
                                        <th className="px-2 py-1 text-right">
                                          Base Cost
                                        </th>
                                        <th className="px-2 py-1 text-right">
                                          Inflation Factor
                                        </th>
                                        <th className="px-2 py-1 text-right">
                                          Inflated Cost
                                        </th>
                                        <th className="px-2 py-1 text-left">
                                          Formula
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {simulationResults.summary.calculationLog.yearlyBreakdown.map(
                                        (year, idx) => (
                                          <tr key={idx}>
                                            <td className="px-2 py-1">
                                              {year.year}
                                            </td>
                                            <td className="px-2 py-1 text-right">
                                              {formatCurrency(
                                                parseFloat(year.baseCost),
                                              )}
                                            </td>
                                            <td className="px-2 py-1 text-right font-mono">
                                              {year.inflationFactor}
                                            </td>
                                            <td className="px-2 py-1 text-right font-semibold">
                                              {formatCurrency(
                                                parseFloat(year.inflatedCost),
                                              )}
                                            </td>
                                            <td className="px-2 py-1 text-left font-mono text-xs">
                                              {year.formula}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-semibold">
                                      <tr>
                                        <td className="px-2 py-1">Total</td>
                                        <td className="px-2 py-1 text-right">
                                          {formatCurrency(
                                            simulationResults.summary
                                              .calculationLog.totalBaseCost,
                                          )}
                                        </td>
                                        <td className="px-2 py-1 text-right">
                                          -
                                        </td>
                                        <td className="px-2 py-1 text-right">
                                          {formatCurrency(
                                            simulationResults.summary
                                              .calculationLog.totalInflatedCost,
                                          )}
                                        </td>
                                        <td className="px-2 py-1"></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {renderAuditTrailSection()}

          {/* Calculation Log Panel */}
          {simulationResults.mode !== "monte_carlo" &&
            isAuditResult &&
            !hasAuditTrailRows && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <button
                  onClick={() =>
                    setIsCalculationDetailsCollapsed(
                      !isCalculationDetailsCollapsed,
                    )
                  }
                  className="flex items-center justify-between w-full text-left mb-4"
                >
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faWrench}
                      className="text-blue-600"
                    />
                    Calculation Log
                  </h2>
                  <FontAwesomeIcon
                    icon={
                      isCalculationDetailsCollapsed
                        ? faChevronRight
                        : faChevronDown
                    }
                    className="text-gray-400"
                  />
                </button>

                {!isCalculationDetailsCollapsed && (
                  <div className="mt-4 space-y-4">
                    <div className="text-sm text-gray-600 mb-4">
                      Detailed calculation steps and formulas used in the
                      simulation. This log helps verify the calculation process.
                    </div>
                    {/* Extract calculation logs from yearly results */}
                    {simulationResults.yearlyResults &&
                      simulationResults.yearlyResults.length > 0 && (
                        <div className="space-y-6 max-h-96 overflow-y-auto">
                          {simulationResults.yearlyResults
                            .filter(
                              (yr) =>
                                yr.spanResults &&
                                yr.spanResults.some(
                                  (sr) => sr.calculationDetails,
                                ),
                            )
                            .map((yearResult, yearIdx) => (
                              <div
                                key={yearIdx}
                                className="border-l-4 border-blue-500 pl-4"
                              >
                                <h3 className="font-semibold text-gray-900 mb-2">
                                  Year {yearResult.year}
                                </h3>

                                {yearResult.spanResults
                                  .filter(
                                    (sr) =>
                                      sr.calculationDetails && sr.totalCost > 0,
                                  )
                                  .map((spanResult, spanIdx) => (
                                    <div
                                      key={spanIdx}
                                      className="mb-4 ml-4 p-3 bg-gray-50 rounded"
                                    >
                                      <div className="font-medium text-gray-800 mb-2">
                                        {spanResult.spanCode ||
                                          spanResult.spanName ||
                                          `Element ${spanResult.spanId}`}
                                      </div>

                                      {spanResult.calculationDetails && (
                                        <div className="text-sm space-y-3">
                                          <div>
                                            <span className="font-medium text-gray-700">
                                              Input Values:
                                            </span>
                                            <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                              <li>
                                                Total Area:{" "}
                                                {
                                                  spanResult.calculationDetails
                                                    .totalArea
                                                }{" "}
                                                m²
                                              </li>
                                              <li>
                                                Corrosion Category:{" "}
                                                {spanResult.calculationDetails
                                                  .corrosionCategory || "N/A"}
                                              </li>
                                              <li>
                                                Damage Level:{" "}
                                                {spanResult.calculationDetails
                                                  .damageLevel || "N/A"}
                                              </li>
                                              <li>
                                                Damage Split: A=
                                                {spanResult.calculationDetails
                                                  .damageSplit?.A ?? 0}
                                                %, B=
                                                {spanResult.calculationDetails
                                                  .damageSplit?.B ?? 0}
                                                %, C=
                                                {spanResult.calculationDetails
                                                  .damageSplit?.C ?? 0}
                                                %
                                              </li>
                                              <li>
                                                Confined Space Multiplier:{" "}
                                                {spanResult.calculationDetails
                                                  .confinedSpaceMultiplier ?? 1}
                                              </li>
                                              <li>
                                                Years Since Last Repair:{" "}
                                                {spanResult.yearsSinceRepair ??
                                                  0}
                                              </li>
                                            </ul>
                                          </div>

                                          {spanResult.calculationDetails
                                            .degradationFormula && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Degradation:
                                              </span>
                                              <div className="ml-4 mt-1 text-gray-600 font-mono text-xs">
                                                {
                                                  spanResult.calculationDetails
                                                    .degradationFormula
                                                }
                                              </div>
                                              <div className="ml-4 mt-1 text-gray-600">
                                                Result:{" "}
                                                {typeof spanResult
                                                  .calculationDetails
                                                  .degradationResult ===
                                                "number"
                                                  ? spanResult.calculationDetails.degradationResult.toFixed(
                                                      2,
                                                    )
                                                  : (spanResult
                                                      .calculationDetails
                                                      .degradationResult ?? 0)}
                                                %
                                              </div>
                                            </div>
                                          )}

                                          <div>
                                            <span className="font-medium text-gray-700">
                                              Repair Decision:
                                            </span>
                                            <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                              <li>
                                                Trigger Threshold (RE, from New
                                                Simulation Triggers):{" "}
                                                {spanResult.calculationDetails
                                                  .decayThreshold ?? 0}
                                                %
                                              </li>
                                              <li>
                                                Threshold Reached:{" "}
                                                {spanResult.calculationDetails
                                                  .thresholdReached
                                                  ? "Yes"
                                                  : "No"}
                                              </li>
                                            </ul>
                                          </div>

                                          {spanResult.calculationDetails
                                            .degradedAreaCalculation && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Degraded Area (m²):
                                              </span>
                                              <div className="ml-4 mt-1 text-gray-600 font-mono text-xs">
                                                {
                                                  spanResult.calculationDetails
                                                    .degradedAreaCalculation
                                                }
                                              </div>
                                            </div>
                                          )}

                                          {spanResult.calculationDetails
                                            .paintedAreaCalculation && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Configured Painted Area (m²):
                                              </span>
                                              <div className="ml-4 mt-1 text-gray-600 font-mono text-xs">
                                                {
                                                  spanResult.calculationDetails
                                                    .paintedAreaCalculation
                                                }
                                              </div>
                                            </div>
                                          )}

                                          {(spanResult.calculationDetails
                                            .areaAFormula ||
                                            spanResult.calculationDetails
                                              .areaBFormula ||
                                            spanResult.calculationDetails
                                              .areaCFormula) && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Area Split (A/B/C):
                                              </span>
                                              <ul className="ml-4 mt-1 space-y-1 text-gray-600 font-mono text-xs">
                                                {spanResult.calculationDetails
                                                  .areaAFormula && (
                                                  <li>
                                                    Area A:{" "}
                                                    {
                                                      spanResult
                                                        .calculationDetails
                                                        .areaAFormula
                                                    }
                                                  </li>
                                                )}
                                                {spanResult.calculationDetails
                                                  .areaBFormula && (
                                                  <li>
                                                    Area B:{" "}
                                                    {
                                                      spanResult
                                                        .calculationDetails
                                                        .areaBFormula
                                                    }
                                                  </li>
                                                )}
                                                {spanResult.calculationDetails
                                                  .areaCFormula && (
                                                  <li>
                                                    Area C:{" "}
                                                    {
                                                      spanResult
                                                        .calculationDetails
                                                        .areaCFormula
                                                    }
                                                  </li>
                                                )}
                                              </ul>
                                            </div>
                                          )}

                                          {Array.isArray(
                                            spanResult.calculationDetails
                                              .costCalculationSteps,
                                          ) &&
                                            spanResult.calculationDetails
                                              .costCalculationSteps.length >
                                              0 && (
                                              <div>
                                                <span className="font-medium text-gray-700">
                                                  Cost Calculation:
                                                </span>
                                                <ul className="ml-4 mt-1 space-y-1 text-gray-600 font-mono text-xs">
                                                  {spanResult.calculationDetails.costCalculationSteps.map(
                                                    (step, i) => (
                                                      <li key={i}>{step}</li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}

                                          {!spanResult.calculationDetails
                                            .costCalculationSteps &&
                                            spanResult.totalCost > 0 && (
                                              <div>
                                                <span className="font-medium text-gray-700">
                                                  Cost Breakdown:
                                                </span>
                                                <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                                  {typeof spanResult.costA ===
                                                    "number" && (
                                                    <li>
                                                      Cost A:{" "}
                                                      {formatCurrency(
                                                        spanResult.costA,
                                                      )}
                                                    </li>
                                                  )}
                                                  {typeof spanResult.costB ===
                                                    "number" && (
                                                    <li>
                                                      Cost B:{" "}
                                                      {formatCurrency(
                                                        spanResult.costB,
                                                      )}
                                                    </li>
                                                  )}
                                                  {typeof spanResult.costC ===
                                                    "number" && (
                                                    <li>
                                                      Cost C:{" "}
                                                      {formatCurrency(
                                                        spanResult.costC,
                                                      )}
                                                    </li>
                                                  )}
                                                  <li>
                                                    Wash:{" "}
                                                    {formatCurrency(
                                                      spanResult.costWash ?? 0,
                                                    )}
                                                  </li>
                                                  <li>
                                                    Total:{" "}
                                                    {formatCurrency(
                                                      spanResult.totalCost ?? 0,
                                                    )}
                                                  </li>
                                                </ul>
                                              </div>
                                            )}

                                          {spanResult.materialCost > 0 && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Cost (legacy):
                                              </span>
                                              <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                                                <li>
                                                  Material:{" "}
                                                  {formatCurrency(
                                                    spanResult.materialCost,
                                                  )}
                                                </li>
                                                <li>
                                                  Labor:{" "}
                                                  {formatCurrency(
                                                    spanResult.laborCost || 0,
                                                  )}
                                                </li>
                                                <li>
                                                  Washing:{" "}
                                                  {formatCurrency(
                                                    spanResult.washingCost || 0,
                                                  )}
                                                </li>
                                                <li>
                                                  Total:{" "}
                                                  {formatCurrency(
                                                    spanResult.totalCost,
                                                  )}
                                                </li>
                                              </ul>
                                            </div>
                                          )}

                                          {(spanResult.repairLabel ||
                                            spanResult.repairType) && (
                                            <div>
                                              <span className="font-medium text-gray-700">
                                                Repair Type:
                                              </span>
                                              <span className="ml-2 text-gray-600 capitalize">
                                                {spanResult.repairLabel ===
                                                "WASH"
                                                  ? "Wash"
                                                  : spanResult.repairLabel ===
                                                      "TU"
                                                    ? "Touch-up"
                                                    : spanResult.repairLabel ===
                                                        "OC"
                                                      ? "Overcoat"
                                                      : spanResult.repairLabel ===
                                                          "RE"
                                                        ? "Recoat"
                                                        : spanResult.repairType ||
                                                          spanResult.repairLabel ||
                                                          ""}
                                              </span>
                                              {spanResult.independentWash && (
                                                <span className="ml-2 text-xs text-amber-600">
                                                  (independent wash)
                                                </span>
                                              )}
                                              {(spanResult.triggeringPortions
                                                ?.length || 0) > 0 && (
                                                <span className="ml-2 text-xs text-amber-700">
                                                  driven by{" "}
                                                  {spanResult.triggeringPortions.join(
                                                    ", ",
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                          )}

                                          <DamagePortionsBreakdown
                                            portions={spanResult.portionStates}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            ))}
                        </div>
                      )}

                    {(!simulationResults.yearlyResults ||
                      !simulationResults.yearlyResults.some(
                        (yr) =>
                          yr.spanResults &&
                          yr.spanResults.some((sr) => sr.calculationDetails),
                      )) && (
                      <div className="text-center py-8 text-gray-500">
                        <p>
                          No calculation details available for this simulation.
                        </p>
                        {(simulationResults.spans?.length || 0) >
                        MAX_CALCULATION_LOG_ELEMENTS ? (
                          <p className="text-sm mt-2">
                            Detailed per-element logs are disabled for large
                            runs (
                            {simulationResults.spans.length.toLocaleString()}{" "}
                            {totalAreaDataEntityLabel.toLowerCase()}) to keep
                            responses fast and stable. Select up to{" "}
                            {MAX_CALCULATION_LOG_ELEMENTS.toLocaleString()}{" "}
                            {totalAreaDataEntityLabel.toLowerCase()} for a
                            calculation log.
                          </p>
                        ) : (
                          <p className="text-sm mt-2">
                            Calculation logs are generated when repairs are
                            triggered.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detailed Results Table (Deterministic mode only) */}
          {simulationResults.mode !== "monte_carlo" && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    4. Detailed Results Table
                  </h2>
                  {renderSaveSnapshotButton({ compact: true })}
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  Hierarchical breakdown of simulation results by bridge, span,
                  and repair process. Years are shown as columns with costs for
                  each activity.
                </div>

                {/* Search and Filter Controls */}
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Show Decay %</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showDecayPercent}
                      onClick={() => setShowDecayPercent((prev) => !prev)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showDecayPercent ? "bg-blue-600" : "bg-gray-300"}`}
                      title="Toggle decay percentage visibility"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showDecayPercent ? "translate-x-6" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExpandAllRowsSignal((s) => s + 1)}
                    className="px-3 py-2 text-sm font-medium text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50"
                    title="Expand all rows"
                  >
                    Expand All
                  </button>
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon
                      icon={faFilter}
                      className="text-gray-400"
                    />
                    <select
                      value={resultsFilterType}
                      onChange={(e) => setResultsFilterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Types</option>
                      <option value="bridge">Bridge</option>
                      <option value="asset">Asset</option>
                      <option value="span">Span</option>
                      <option value="panelbay">Panel Bay</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="activity">Activity</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[280px] relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search by asset, span, panel bay, or activity..."
                      value={resultsSearchTerm}
                      onChange={(e) => setResultsSearchTerm(e.target.value)}
                    />
                  </div>
                  {(resultsSearchTerm || resultsFilterType !== "all") && (
                    <button
                      onClick={() => {
                        setResultsSearchTerm("");
                        setResultsFilterType("all");
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <SimulationResultsTable
                  data={simulationResults.hierarchy || null}
                  expandedItems={expandedItems}
                  onToggleExpanded={toggleExpanded}
                  searchTerm={resultsSearchTerm}
                  filterType={resultsFilterType}
                  showDecayPercent={showDecayPercent}
                  expandAllSignal={expandAllRowsSignal}
                  simulationResults={simulationResults}
                  mobilizationData={mobilizationSummary}
                  d610Settings={d610Settings}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {showSaveSnapshotModal && simulationRecordId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSaveSnapshot();
            }}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Save Snapshot
                  </h2>
                  <button
                    type="button"
                    onClick={closeSaveSnapshotModal}
                    disabled={savingSnapshot}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Close save snapshot dialog"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="text-2xl" />
                  </button>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Snapshot Title
                </label>
                <input
                  ref={snapshotTitleInputRef}
                  type="text"
                  value={snapshotTitle}
                  onChange={(e) => setSnapshotTitle(e.target.value)}
                  maxLength={150}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder={snapshotTitlePlaceholder}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Choose the name shown in the saved snapshot list and dashboard.
                </p>
                {!lastRunSelectAllMode && (
                  <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    This snapshot will save the current selection, not the full bridge.
                  </p>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeSaveSnapshotModal}
                    disabled={savingSnapshot}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingSnapshot || !snapshotTitle.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-70"
                  >
                    <FontAwesomeIcon
                      icon={savingSnapshot ? faSpinner : faSave}
                      spin={savingSnapshot}
                    />
                    {savingSnapshot ? "Saving..." : "Save Snapshot"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Repair Project Modal */}
      {showCreateProjectModal && simulationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                  <span>Create Repair Project from Simulation</span>
                  <InlineFieldTooltip
                    page="enhanced_simulation_results"
                    section="modal_titles"
                    fieldKey="create_repair_project"
                    fallbackText="Create a repair project using current simulation results and selections."
                    title="Create Repair Project"
                  />
                </h2>
                <button
                  onClick={() => setShowCreateProjectModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="text-2xl" />
                </button>
              </div>

              {/* Project Details Form */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={projectFormData.projectName}
                    onChange={(e) =>
                      setProjectFormData((prev) => ({
                        ...prev,
                        projectName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Bridge Maintenance 2025"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Code
                    </label>
                    <input
                      type="text"
                      value={projectFormData.projectCode}
                      onChange={(e) =>
                        setProjectFormData((prev) => ({
                          ...prev,
                          projectCode: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Planned Year *
                    </label>
                    <input
                      type="number"
                      value={projectFormData.plannedYear}
                      onChange={(e) =>
                        setProjectFormData((prev) => ({
                          ...prev,
                          plannedYear: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min={new Date().getFullYear()}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Crews Count
                    </label>
                    <input
                      type="number"
                      value={projectFormData.crewsCount}
                      onChange={(e) =>
                        setProjectFormData((prev) => ({
                          ...prev,
                          crewsCount: parseInt(e.target.value) || 1,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={projectFormData.priority}
                      onChange={(e) =>
                        setProjectFormData((prev) => ({
                          ...prev,
                          priority: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={projectFormData.description}
                    onChange={(e) =>
                      setProjectFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Project description..."
                  />
                </div>
              </div>

              {/* Project Summary */}
              {projectFormData.plannedYear &&
                (() => {
                  const plannedYear = projectFormData.plannedYear;
                  const startYear =
                    simulationResults.simulationStartYear != null &&
                    simulationResults.simulationStartYear > 0
                      ? simulationResults.simulationStartYear
                      : new Date().getFullYear();
                  const yearIndex = plannedYear - startYear + 1;
                  const yearResult = simulationResults.yearlyResults?.find(
                    (yr) => yr.year === yearIndex,
                  );
                  const hasRepairs = yearResult && yearResult.totalCost > 0;
                  const elementCount =
                    yearResult?.spanResults?.filter((sr) => sr.totalCost > 0)
                      .length || 0;

                  return (
                    <div
                      className={`border rounded-md p-4 mb-6 ${hasRepairs ? "bg-blue-50 border-blue-200" : "bg-yellow-50 border-yellow-200"}`}
                    >
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Project Summary
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Planned Year:</span>
                          <span className="ml-2 font-medium">
                            {plannedYear}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            Elements with Repairs:
                          </span>
                          <span className="ml-2 font-medium">
                            {elementCount}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Estimated Cost:</span>
                          <span className="ml-2 font-medium">
                            {hasRepairs
                              ? `$${(yearResult.totalCost || 0).toLocaleString()}`
                              : "$0"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Bridge:</span>
                          <span className="ml-2 font-medium">
                            {selectedBridge?.name || "N/A"}
                          </span>
                        </div>
                      </div>
                      {!hasRepairs && (
                        <p className="text-sm text-yellow-700 mt-2">
                          ⚠️ No repairs found for year {plannedYear}. Please
                          select a different year or check the simulation
                          results.
                        </p>
                      )}
                    </div>
                  );
                })()}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateProjectModal(false);
                    setProjectFormData({
                      projectName: "",
                      projectCode: "",
                      description: "",
                      plannedYear: new Date().getFullYear(),
                      crewsCount: 1,
                      priority: "medium",
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRepairProject}
                  disabled={
                    !projectFormData.projectName || !projectFormData.plannedYear
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSimulationResults;
